import React, { useState, useEffect, useContext, useRef } from 'react';
import { SettingsContext } from '../../contexts/SettingsContext';
import ReportGenerator from '../../components/ReportGenerator';
import { 
  useFinancialData, 
  useClientsData, 
  usePackagesData, 
  useReservationsData,
  useSuppliersData,
  useDashboardData,
  resetAndInitializeData
} from '../../services/dataService';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import Chart from 'chart.js/auto'; // Add this import for direct chart rendering
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart as RechartsBarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  RadialBarChart,
  RadialBar,
  ScatterChart,
  Scatter,
  FunnelChart,
  Funnel,
  LabelList,
} from 'recharts';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  CircularProgress,
  Container,

  IconButton,
  Snackbar,
  CardHeader,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  AttachMoney,
  AccountBalanceWallet,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as BankIcon,
  Assessment as AssessmentIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  AccountBalanceWallet as WalletIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  Timeline as TimelineIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Flight as FlightIcon,
  Hotel as HotelIcon,
  Restaurant as RestaurantIcon,
  DirectionsBus as TransportIcon,
  MonetizationOn as RevenueIcon,
  Savings as SavingsIcon,
  CurrencyExchange as ExchangeIcon,
  Analytics as AnalyticsIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Insights as InsightsIcon,
  ShowChart as ShowChartIcon,
  LocalAtm as LocalAtmIcon,
  AccountCircle as AccountCircleIcon,
  TrendingFlat as TrendingFlatIcon,
  CompareArrows as CompareArrowsIcon,
  Speed as SpeedIcon,
  Star as StarIcon,
  ErrorOutline as ErrorOutlineIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  AccessTime as AccessTimeIcon,
  Today as TodayIcon,
  Event as EventIcon,
  DateRange as DateRangeIcon,
  CalendarToday as CalendarTodayIcon,
  Update as UpdateIcon,
  History as HistoryIcon,
  TrendingUp as ForecastIcon,
  PriceChange as PriceChangeIcon,
  RequestQuote as RequestQuoteIcon,

  FileDownload as FileDownloadIcon,

  FilterList as FilterListIcon,
  Dashboard as DashboardIcon,
  Info as InfoIcon,
  EventAvailable as EventAvailableIcon,
  Percent as PercentIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  AccountBalance as AccountBalanceIcon,
  TipsAndUpdates as TipsAndUpdatesIcon,
} from '@mui/icons-material';

// Define chart colors for PDF export
const COLORS = ['#1e40af', '#7c3aed', '#059669', '#f59e0b', '#dc2626', '#6366f1', '#ec4899', '#06b6d4'];

// Add initialFinancialData definition
const initialFinancialData = {
  kpis: {
    revenue: {
      current: 4850000,
      previous: 4200000,
      target: 5000000,
      growth: 15.5
    },
    profit: {
      current: 1250000,
      previous: 980000,
      target: 1500000,
      growth: 27.6
    },
    cashFlow: {
      current: 1750000,
      previous: 1500000,
      target: 2000000,
      growth: 16.7
    },
    // EBITDA section removed as requested
    expenses: {
      current: 3600000,
      previous: 3220000,
      target: 3500000,
      growth: 11.8
    },
    roi: {
      current: 25.8,
      previous: 23.3,
      target: 30.0,
      growth: 2.5
    },
    margin: 25.8,
    marginChange: '+2.5%'
  },
  operationalMetrics: {
    clients: 0, // Will be replaced with real data
    reservations: 0, // Will be replaced with real data
    confirmed: 0, // Will be replaced with real data
    suppliers: 0, // Will be replaced with real data
    activePackages: 0, // Will be replaced with real data
    averageValue: 0, // Will be replaced with real data
    conversionRate: 0 // Will be replaced with real data
  },
  clientMetrics: {
    total: 0, // Will be replaced with real data
    active: 0, // Will be replaced with real data
    new: 0, // Will be replaced with real data
    premium: 0, // Will be replaced with real data
    retention: 0 // Will be replaced with real data
  },
  revenueAnalysis: {
    evolution: [
      { period: 'Jan', amount: 350000 },
      { period: 'Fév', amount: 420000 },
      { period: 'Mar', amount: 380000 },
      { period: 'Avr', amount: 420000 },
      { period: 'Mai', amount: 480000 },
      { period: 'Jun', amount: 520000 },
      { period: 'Jul', amount: 550000 },
      { period: 'Aoû', amount: 500000 },
      { period: 'Sep', amount: 450000 },
      { period: 'Oct', amount: 400000 },
      { period: 'Nov', amount: 380000 },
      { period: 'Déc', amount: 420000 }
    ],
    byService: [
      { name: 'Vols', value: 1800000 },
      { name: 'Hôtels', value: 1200000 },
      { name: 'Packages', value: 950000 },
      { name: 'Omra', value: 550000 },
      { name: 'Visas', value: 350000 }
    ],
    byDestination: [
      { name: 'Europe', value: 1650000 },
      { name: 'Moyen-Orient', value: 1350000 },
      { name: 'Asie', value: 950000 },
      { name: 'Afrique', value: 650000 },
      { name: 'Amérique', value: 250000 }
    ]
  },
  profitabilityAnalysis: {
    margins: [
      { name: 'Vols', value: 22.5 },
      { name: 'Hôtels', value: 28.3 },
      { name: 'Packages', value: 32.1 },
      { name: 'Omra', value: 35.2 },
      { name: 'Visas', value: 42.5 }
    ],
    trends: [
      { period: 'Jan', margin: 24.5 },
      { period: 'Fév', margin: 25.1 },
      { period: 'Mar', margin: 25.8 },
      { period: 'Avr', margin: 26.2 },
      { period: 'Mai', margin: 26.5 },
      { period: 'Jun', margin: 27.0 },
      { period: 'Jul', margin: 27.6 },
      { period: 'Aoû', margin: 28.1 },
      { period: 'Sep', margin: 28.4 },
      { period: 'Oct', margin: 28.8 },
      { period: 'Nov', margin: 29.2 },
      { period: 'Déc', margin: 29.5 }
    ]
  },
  topPackages: [
    {
      nom: 'Circuit Istanbul',
      destination: 'Istanbul, Turquie',
      prix: 85000, // Updated to match real data
      marge: 32.5,
      revenus: 1275000 // Recalculated based on correct price
    },
    {
      nom: 'Dubaï Luxe',
      destination: 'Dubaï, EAU',
      prix: 160000,
      marge: 29.0,
      revenus: 1920000
    },
    {
      nom: 'Paris Weekend',
      destination: 'Paris, France',
      prix: 72500,
      marge: 35.2,
      revenus: 580000
    },
    {
      nom: 'Omra VIP',
      destination: 'La Mecque, Arabie Saoudite',
      prix: 120000,
      marge: 30.1,
      revenus: 4200000
    },
    {
      nom: 'Marrakech Royal',
      destination: 'Marrakech, Maroc',
      prix: 67000,
      marge: 28.7,
      revenus: 1474000
    }
  ]
};

// CategoryPieChart component for displaying transaction categories
const CategoryPieChart = ({ calculatedFinancialData, financialData, colors, renderChart, formatCurrency }) => {
  // Generate pie chart data
  const pieData = React.useMemo(() => {
    // First try to use calculated data
    if (calculatedFinancialData?.transactionAnalysis?.categories && 
        calculatedFinancialData.transactionAnalysis.categories.length > 0) {
      console.log('Using calculated categories for pie chart');
      return calculatedFinancialData.transactionAnalysis.categories;
    }
    
    // Try to generate from financial data directly
    if (financialData && financialData.length > 0) {
      console.log('Generating categories from raw financial data');
      
      // Group by category
      const categories = {};
      financialData.forEach(transaction => {
        const category = transaction.categorie || 
                        (transaction.type === 'Recette' ? 'Revenus' : 'Dépenses');
        const amount = parseFloat(transaction.montant) || 0;
        
        if (!categories[category]) categories[category] = 0;
        categories[category] += amount;
      });
      
      // Convert to array format
      return Object.keys(categories).map(name => ({
        name,
        value: categories[name]
      }));
    }
    
    // Fallback to sample data
    console.log('Using sample data for pie chart');
    return [
      { name: 'Hôtels', value: 250000 },
      { name: 'Vols', value: 370000 },
      { name: 'Packages', value: 200000 },
      { name: 'Omra', value: 110000 },
      { name: 'Visas', value: 70000 }
    ];
  }, [calculatedFinancialData, financialData]);
  
  console.log('Final pie chart data:', pieData);
  
  return renderChart("Répartition par Catégorie", 
    <ResponsiveContainer width="100%" height={350}>
      <RechartsPieChart>
        <Pie
          data={pieData}
          cx="50%"
          cy="50%"
          labelLine={true}
          outerRadius={100}
          fill="#8884d8"
          dataKey="value"
          nameKey="name"
          label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
        >
          {pieData.map((entry, index) => (
            <Cell 
              key={`cell-${index}`} 
              fill={colors.chart[index % colors.chart.length]} 
            />
          ))}
        </Pie>
        <RechartsTooltip formatter={(value) => `${value.toLocaleString()} DZD`} />
        <Legend />
      </RechartsPieChart>
    </ResponsiveContainer>
  );
};

// Helper function to process transaction categories for pie chart
const getTransactionCategories = (rawTransactions) => {
  console.log('Processing transaction categories from', rawTransactions?.length || 0, 'transactions');
  
  // Default sample data
  const defaultCategories = [
    { name: 'Hôtels', value: 25 },
    { name: 'Vols', value: 37 },
    { name: 'Packages', value: 20 },
    { name: 'Omra', value: 11 },
    { name: 'Visas', value: 7 }
  ];
  
  if (!rawTransactions || rawTransactions.length === 0) {
    console.log('No transactions available for category processing, using default data');
    return defaultCategories;
  }
  
  try {
    // Group transactions by category
    const categoriesMap = {};
    let hasValidCategories = false;
    
    // First pass: identify categories and calculate totals
    rawTransactions.forEach(transaction => {
      // Use either categorie field or derive from other fields
      let category = transaction.categorie;
      
      // If no categorie field, try to derive from other fields
      if (!category) {
        if (transaction.reservationId) {
          // Try to determine category from reservation details
          if (transaction.forfaitNom && transaction.forfaitNom.toLowerCase().includes('vol')) {
            category = 'Vols';
          } else if (transaction.forfaitNom && transaction.forfaitNom.toLowerCase().includes('hotel')) {
            category = 'Hôtels';
          } else if (transaction.forfaitNom && transaction.forfaitNom.toLowerCase().includes('omra')) {
            category = 'Omra';
          } else if (transaction.forfaitNom && transaction.forfaitNom.toLowerCase().includes('visa')) {
            category = 'Visas';
          } else {
            category = 'Packages';
          }
        } else if (transaction.fournisseurNom) {
          // Try to determine category from supplier type
          if (transaction.fournisseurType === 'Hôtel') {
            category = 'Hôtels';
          } else if (transaction.fournisseurType === 'Transport') {
            category = 'Vols';
          } else {
            category = 'Services';
          }
        } else {
          // Default categorization based on transaction type
          category = transaction.type === 'Recette' ? 'Revenus Divers' : 'Dépenses Diverses';
        }
      }
      
      // Only process transactions with amounts
      const amount = parseFloat(transaction.montant) || 0;
      if (amount <= 0) return;
      
      if (!categoriesMap[category]) {
        categoriesMap[category] = 0;
      }
      
      categoriesMap[category] += amount;
      hasValidCategories = true;
    });
    
    if (!hasValidCategories) {
      console.log('No valid categories found in transactions, using default data');
      return defaultCategories;
    }
    
    // Convert to array format for pie chart
    const categoriesData = Object.keys(categoriesMap).map(category => ({
      name: category,
      value: categoriesMap[category]
    }));
    
    // Sort by value (descending)
    categoriesData.sort((a, b) => b.value - a.value);
    
    console.log('Processed transaction categories:', categoriesData);
    
    // If no categories were found, return sample data
    if (categoriesData.length === 0) {
      return defaultCategories;
    }
    
    return categoriesData;
  } catch (error) {
    console.error('Error processing transaction categories:', error);
    return defaultCategories;
  }
};

// Helper function to get transaction chart data
const getTransactionChartData = (calculatedFinancialData, financialStats, rawTransactions) => {
  console.log('getTransactionChartData called with:', { 
    hasCalculatedData: !!calculatedFinancialData, 
    hasFinancialStats: !!financialStats,
    rawTransactionsCount: rawTransactions?.length || 0
  });
  
  // First try to use raw transaction data from the finances page
  if (rawTransactions && rawTransactions.length > 0) {
    console.log('Using raw transaction data from finances page');
    
    const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    const transactionsByMonth = {};
    
    // Initialize all months with zero values
    monthNames.forEach(month => {
      transactionsByMonth[month] = { month, recettes: 0, depenses: 0, total: 0 };
    });
    
    // Process each transaction
    rawTransactions.forEach(transaction => {
      try {
        if (!transaction.date) return;
        
        const date = new Date(transaction.date);
        if (isNaN(date.getTime())) return;
        
        const month = monthNames[date.getMonth()];
        const amount = parseFloat(transaction.montant) || 0;
        
        if (transaction.type === 'Recette') {
          transactionsByMonth[month].recettes += amount;
          transactionsByMonth[month].total += amount;
        } else if (transaction.type === 'Dépense') {
          transactionsByMonth[month].depenses += amount;
          transactionsByMonth[month].total -= amount;
        }
      } catch (error) {
        console.error('Error processing transaction:', error);
      }
    });
    
    const result = Object.values(transactionsByMonth);
    console.log('Processed transaction data:', result);
    
    // Check if we have real data
    if (result.some(item => item.recettes > 0 || item.depenses > 0)) {
      return result;
    }
    
    console.log('No actual transaction data found in raw transactions');
  }
  
  // Fallback to calculated data if available - with proper null checks
  if (calculatedFinancialData?.transactionAnalysis?.evolution && 
      calculatedFinancialData.transactionAnalysis.evolution.length > 0 &&
      !calculatedFinancialData.transactionAnalysis.evolution.every(item => item.recettes === 0 && item.depenses === 0)) {
    console.log('Using calculated financial data for chart');
    return calculatedFinancialData.transactionAnalysis.evolution;
  }
  
  // Last resort: generate sample data
  console.log('Generating sample data for chart');
  const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
  const totalRevenue = financialStats?.totalRevenue || 4850000;
  const totalExpenses = financialStats?.totalExpenses || 3600000;
  
  return monthNames.map((month, index) => {
    // Create seasonal variation
    const seasonalFactor = index < 3 ? 0.8 : // Jan-Mar: Low season
                         index < 6 ? 1.2 : // Apr-Jun: High season
                         index < 9 ? 1.5 : // Jul-Sep: Peak season
                         1.3;  // Oct-Dec: Holiday season
    
    const monthRevenue = (totalRevenue / 12) * seasonalFactor * (0.9 + Math.random() * 0.2);
    const monthExpenses = (totalExpenses / 12) * seasonalFactor * (0.85 + Math.random() * 0.3);
    
    return {
      month,
      recettes: Math.round(monthRevenue),
      depenses: Math.round(monthExpenses),
      total: Math.round(monthRevenue - monthExpenses)
    };
  });
};

const FinancesReports = () => {
  const { settings, formatCurrency, formatDate, calculateTotalWithTax, calculateCommission } = useContext(SettingsContext);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedCurrency, setSelectedCurrency] = useState('DZD');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [realTimeUpdate, setRealTimeUpdate] = useState(new Date());
  const [calculatedFinancialData, setCalculatedFinancialData] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const reportRef = useRef(null);
  
  // Define notification handlers at the beginning to avoid reference errors
  const handleNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
    
    // Auto-hide after 6 seconds
    setTimeout(() => {
      setNotification(prev => ({ ...prev, open: false }));
    }, 6000);
  };
  
  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  // Use data service hooks to get real-time data
  const { transactions: financialData, stats: financialStats, loading: financialLoading } = useFinancialData();
  const { clientsData, loading: clientsLoading } = useClientsData();
  const { packagesData, loading: packagesLoading } = usePackagesData();
  const { reservationsData, loading: reservationsLoading } = useReservationsData();
  const { suppliersData, loading: suppliersLoading } = useSuppliersData();
  const { dashboardData, loading: dashboardLoading } = useDashboardData();
  
  // Log data availability and ensure we have transaction data
  // Make sure transactionAnalysis always exists in calculatedFinancialData
  useEffect(() => {
    if (calculatedFinancialData && !calculatedFinancialData.transactionAnalysis) {
      console.log('Adding missing transactionAnalysis structure');
      setCalculatedFinancialData(prevData => ({
        ...prevData,
        transactionAnalysis: {
          categories: [],
          evolution: []
        }
      }));
    }
  }, [calculatedFinancialData]);
  
  useEffect(() => {
    console.log('Financial data from hook:', financialData);
    console.log('Financial stats:', financialStats);
    
    // If we have financial data, calculate metrics
    if (financialData && financialData.length > 0) {
      console.log('Financial data available, calculating metrics');
      calculateFinancialMetrics();
    } else {
      console.log('No financial data available, checking localStorage');
      // Try to get data from localStorage
      try {
        const localTransactions = localStorage.getItem('transactionsData');
        if (localTransactions) {
          console.log('Found transactions in localStorage');
          calculateFinancialMetrics();
        } else {
          console.log('No transactions in localStorage, initializing data');
          resetAndInitializeData();
          // Calculate metrics after a short delay to allow data to load
          setTimeout(() => {
            calculateFinancialMetrics();
          }, 500);
        }
      } catch (error) {
        console.error('Error checking localStorage:', error);
      }
    }
  }, [financialData, financialStats]);

  // Set loading state based on all data sources and initialize data if needed
  useEffect(() => {
    console.log('Data loading states:', {
      financialLoading,
      clientsLoading,
      packagesLoading,
      reservationsLoading,
      suppliersLoading,
      dashboardLoading
    });
    
    console.log('Current data counts:', {
      clientsCount: clientsData?.length || 0,
      reservationsCount: reservationsData?.length || 0,
      suppliersCount: suppliersData?.length || 0,
      packagesCount: packagesData?.length || 0
    });
    
    // Do not clear localStorage on every mount – this erased data before charts rendered
    // localStorage.clear(); // Uncomment only when you explicitly need to reset cached data
    
        // We'll handle data initialization in the financial data effect
    
    // Set loading state
    setLoading(
      financialLoading || 
      clientsLoading || 
      packagesLoading || 
      reservationsLoading || 
      suppliersLoading || 
      dashboardLoading
    );
    
        // Allow hooks to finish fetching before giving up; remove forced timeout that could stop charts from rendering
    // const safetyTimeout = setTimeout(() => {/* fallback logic */}, 3000);
    // return () => clearTimeout(safetyTimeout);
  }, []);

  // Process and calculate financial metrics when data changes
  useEffect(() => {
    // Update loading state whenever dependencies change
    const isLoading = financialLoading || clientsLoading || packagesLoading || 
                     reservationsLoading || suppliersLoading || dashboardLoading;
    setLoading(isLoading);

    // Only proceed if we have some data loaded
    if (!isLoading && (financialData?.length || clientsData?.length || reservationsData?.length)) {
      console.log('Data dependencies updated, recalculating financial metrics');
        calculateFinancialMetrics();
        setRealTimeUpdate(new Date());
      
      // Broadcast an update so other components can refresh if needed
      window.dispatchEvent(new CustomEvent('financialMetricsUpdate', {
        detail: { timestamp: new Date().toISOString() }
      }));
    } else if (!isLoading) {
      // If no data is available but loading finished, use initialFinancialData
        console.log('No data available, using initialFinancialData');
        setCalculatedFinancialData(initialFinancialData);
      setLoading(false);
      }
  }, [financialData, clientsData, packagesData, reservationsData, suppliersData, dashboardData, 
      financialLoading, clientsLoading, packagesLoading, reservationsLoading, suppliersLoading, dashboardLoading]);
  
  // Listen for data updates from other components
  useEffect(() => {
    const handleDataUpdate = (e) => {
      console.log('Data update event received in Finance Reports page:', e.type);
      // Force reload of data and recalculation of financial metrics
      calculateFinancialMetrics();
      setRealTimeUpdate(new Date());
      
      // Show notification to user
      handleNotification(`Données mises à jour depuis ${e.type.replace('DataUpdate', '')}`, 'info');
      
      // Refresh data without wiping storage
      if (financialStats?.totalRevenue) {
        setCalculatedFinancialData(prevData => ({
          ...prevData,
          kpis: {
            ...prevData.kpis,
            revenue: {
              ...prevData.kpis.revenue,
              current: financialStats.totalRevenue,
              previous: financialStats.totalRevenue * 0.85,
              target: financialStats.totalRevenue * 1.03
            },
            profit: {
              ...prevData.kpis.profit,
              current: financialStats.netProfit,
              previous: financialStats.netProfit * 0.78,
              target: financialStats.netProfit * 1.2
            },
            expenses: {
              ...prevData.kpis.expenses,
              current: financialStats.totalExpenses,
              previous: financialStats.totalExpenses * 0.9,
              target: financialStats.totalExpenses * 0.97
            }
          }
        }));
      }
    };
    
    // Register event listeners for all data types
    window.addEventListener('reservationDataUpdate', handleDataUpdate);
    window.addEventListener('clientDataUpdate', handleDataUpdate);
    window.addEventListener('packageDataUpdate', handleDataUpdate);
    window.addEventListener('supplierDataUpdate', handleDataUpdate);
    window.addEventListener('financialDataUpdate', handleDataUpdate);
    window.addEventListener('calendarDataUpdate', handleDataUpdate);
    window.addEventListener('dashboardDataUpdate', handleDataUpdate);
    window.addEventListener('documentDataUpdate', handleDataUpdate);
    window.addEventListener('reportDataUpdate', handleDataUpdate);
    
    // Also listen for localStorage changes with improved handling
    window.addEventListener('storage', (e) => {
      if (e.key && (
        e.key.includes('Data') || 
        e.key === 'dataCache' || 
        e.key === 'calendarEvents'
      )) {
        console.log('Storage change detected in Finance Reports page:', e.key);
        // Force reload of data and recalculation of financial metrics
        calculateFinancialMetrics();
      setRealTimeUpdate(new Date());
        
        // Show notification to user
        handleNotification(`Données mises à jour depuis ${e.key.replace('Data', '')}`, 'info');
        
        // Also explicitly pull fresh data from other services
        const sourceType = e.key.replace('Data', '').toLowerCase();
        // No storage wipe needed; hooks will pick up latest data automatically
        if (sourceType) {
          console.log(`Storage change for ${sourceType} detected – hooks will refresh automatically.`);
        }
      }
    });
    
    // Set up a more frequent periodic refresh (every 10 seconds)
    const refreshInterval = setInterval(() => {
      console.log('Periodic refresh of financial data');
      calculateFinancialMetrics();
      setRealTimeUpdate(new Date());
    }, 10000);

    return () => {
      // Clean up event listeners and interval
      window.removeEventListener('reservationDataUpdate', handleDataUpdate);
      window.removeEventListener('clientDataUpdate', handleDataUpdate);
      window.removeEventListener('packageDataUpdate', handleDataUpdate);
      window.removeEventListener('supplierDataUpdate', handleDataUpdate);
      window.removeEventListener('financialDataUpdate', handleDataUpdate);
      window.removeEventListener('calendarDataUpdate', handleDataUpdate);
      window.removeEventListener('storage', handleDataUpdate);
      clearInterval(refreshInterval);
    };
  }, []);
  // Helper function to calculate financial metrics with fresh data
const calculateWithFreshData = (freshReservations, freshClients, freshPackages, freshSuppliers) => {
  console.log('Calculating with fresh data:', {
    reservations: freshReservations.length,
    clients: freshClients.length,
    packages: freshPackages.length,
    suppliers: freshSuppliers?.length || 0
  });
  
  // Use the fresh data to calculate metrics
  const totalRevenue = freshReservations.reduce((sum, res) => sum + (res.prixTotal || 0), 0);
  const totalExpenses = totalRevenue * 0.65; // Approximation for expenses
  const profit = totalRevenue - totalExpenses;
  const cashFlow = totalRevenue * 0.36;
  const roi = totalExpenses > 0 ? (profit / totalExpenses) * 100 : 0;
  
  // Create the financial data structure
  const calculatedData = {
    kpis: {
      revenue: {
        current: totalRevenue || 0,
        previous: totalRevenue * 0.85 || 0,
        target: totalRevenue * 1.03 || 0,
        growth: 15.5
      },
      profit: {
        current: profit || 0,
        previous: profit * 0.78 || 0,
        target: profit * 1.2 || 0,
        growth: 27.6
      },
      cashFlow: {
        current: cashFlow || 0,
        previous: cashFlow * 0.85 || 0,
        target: cashFlow * 1.15 || 0,
        growth: 16.7
      },
      // EBITDA section removed as requested
      expenses: {
        current: totalExpenses || 0,
        previous: totalExpenses * 0.9 || 0,
        target: totalExpenses * 0.97 || 0,
        growth: 11.8
      },
      roi: {
        current: roi || 0,
        previous: roi * 0.9 || 0,
        target: roi * 1.15 || 0,
        growth: 2.5
      },
      margin: profit / totalRevenue * 100 || 0,
      marginChange: '+2.5%'
    },
    operationalMetrics: {
      clients: freshClients.length || 0,
      reservations: freshReservations.length || 0,
      confirmed: freshReservations.filter(res => res.statut === 'Confirmée').length || 0,
      suppliers: freshSuppliers?.length || 0,
      activePackages: freshPackages.length || 0,
      averageValue: freshReservations.length > 0
        ? freshReservations.reduce((sum, res) => sum + (res.prixTotal || 0), 0) / freshReservations.length
        : 0,
      conversionRate: 68.5
    },
    clientMetrics: {
      total: freshClients.length || 0,
      active: freshClients.filter(client => client.statut === 'Actif').length || 0,
      new: freshClients.filter(client => {
        const inscriptionDate = new Date(client.dateInscription);
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        return inscriptionDate > threeMonthsAgo;
      }).length || 0,
      premium: freshClients.filter(client => client.niveauFidelite === 'Premium').length || 0,
      retention: 82.5
    }
  };
  
  // Set the calculated data in state
  setCalculatedFinancialData(calculatedData);
  setLoading(false);
  
  // Also dispatch event to notify other components
  window.dispatchEvent(new CustomEvent('financialMetricsCalculated', {
    detail: { timestamp: new Date().toISOString() }
  }));
  
  return calculatedData;
};

// Enhanced calculation of financial metrics with better real-time data handling
  const calculateFinancialMetrics = () => {
    console.log('Calculating financial metrics with real data');
    console.log('Clients data:', clientsData?.length || 0, 'clients');
    console.log('Reservations data:', reservationsData?.length || 0, 'reservations');
    console.log('Suppliers data:', suppliersData?.length || 0, 'suppliers');
    
    // If hooks are still loading, show placeholder but set fallback data so spinner stops
    if (!clientsData || !reservationsData || !suppliersData) {
      setCalculatedFinancialData({
        ...initialFinancialData,
        kpis: {
          ...initialFinancialData.kpis,
          revenue: {
            ...initialFinancialData.kpis.revenue,
            current: financialStats?.totalRevenue || 0,
            previous: (financialStats?.totalRevenue || 0) * 0.85,
            target: (financialStats?.totalRevenue || 0) * 1.03
          },
          profit: {
            ...initialFinancialData.kpis.profit,
            current: financialStats?.netProfit || 0,
            previous: (financialStats?.netProfit || 0) * 0.78,
            target: (financialStats?.netProfit || 0) * 1.2
          },
          expenses: {
            ...initialFinancialData.kpis.expenses,
            current: financialStats?.totalExpenses || 0,
            previous: (financialStats?.totalExpenses || 0) * 0.9,
            target: (financialStats?.totalExpenses || 0) * 0.97
          }
        }
      });
      return;
    }
    
    // If arrays are still empty (hooks finished but no data), just return the fallback data
    if (clientsData.length === 0 || reservationsData.length === 0 || suppliersData.length === 0) {
      setCalculatedFinancialData(initialFinancialData);
      setLoading(false);
      return initialFinancialData;
    }
    
    try {
            // Use pre-computed stats from useFinancialData (same as Finances page)
      const totalRevenue = financialStats?.totalRevenue || 0;
      const totalExpenses = financialStats?.totalExpenses || 0;
      
      // Calculate profit
      const profit = totalRevenue - totalExpenses;
      
      // EBITDA calculation removed as requested
      
      // Calculate cash flow (simplified)
      const cashFlow = totalRevenue * 0.36;
      
      // Calculate ROI
      const roi = totalExpenses > 0 ? (profit / totalExpenses) * 100 : 0;

      // Create the financial data structure
      const calculatedData = {
        kpis: {
          revenue: {
            current: totalRevenue || 0,
            previous: totalRevenue * 0.85 || 0, // Approximate previous period
            target: totalRevenue * 1.03 || 0,    // Target is 3% above current
            growth: 15.5                        // Approximate growth
          },
          profit: {
            current: profit || 0,
            previous: profit * 0.78 || 0,
            target: profit * 1.2 || 0,
            growth: 27.6
          },
          cashFlow: {
            current: cashFlow || 0,
            previous: cashFlow * 0.85 || 0,
            target: cashFlow * 1.15 || 0,
            growth: 16.7
          },
          // EBITDA section removed as requested
          expenses: {
            current: totalExpenses || 0,
            previous: totalExpenses * 0.9 || 0,
            target: totalExpenses * 0.97 || 0, // Target is 3% below current
            growth: 11.8
          },
          roi: {
            current: roi || 0,
            previous: roi * 0.9 || 0,
            target: roi * 1.15 || 0,
            growth: 2.5
          },
          margin: profit / totalRevenue * 100 || 0,
          marginChange: '+2.5%'
        },
        operationalMetrics: {
          clients: clientsData.length || 0,
          reservations: reservationsData.length || 0,
          confirmed: reservationsData.filter(res => res.statut === 'Confirmée').length || 0,
          suppliers: suppliersData.length || 0,
          activePackages: packagesData.length || 0,
          averageValue: reservationsData.length > 0 
            ? reservationsData.reduce((sum, res) => sum + (res.prixTotal || 0), 0) / reservationsData.length 
            : 0,
          conversionRate: 68.5 // This would need real data
        },
        clientMetrics: {
          total: clientsData.length || 0,
          active: clientsData.filter(client => client.statut === 'Actif').length || 0,
          new: clientsData.filter(client => {
            const inscriptionDate = new Date(client.dateInscription);
            const threeMonthsAgo = new Date();
            threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
            return inscriptionDate > threeMonthsAgo;
          }).length || 0,
          premium: clientsData.filter(client => client.niveauFidelite === 'Premium').length || 0,
          retention: 82.5 // This would need historical data
        }
      };
      
      // Generate top packages data based on real package data
      const topPackagesData = [];
      
      // Try to find the Istanbul package from real data
      const istanbulPackage = packagesData.find(pkg => 
        pkg.destination && pkg.destination.includes('Istanbul') || 
        (pkg.titre && pkg.titre.includes('Istanbul')));
      
      if (istanbulPackage) {
        topPackagesData.push({
          nom: istanbulPackage.titre || 'Circuit Istanbul',
          destination: istanbulPackage.destination || 'Istanbul, Turquie',
          prix: istanbulPackage.prix || 85000, // Use actual price from package data
          marge: 32.5,
          revenus: (istanbulPackage.prix || 85000) * 15 // Approximate revenue based on actual price
        });
      } else {
        // Fallback if not found in real data
        topPackagesData.push({
          nom: 'Circuit Istanbul',
          destination: 'Istanbul, Turquie',
          prix: 85000, // Use correct price from sample data
          marge: 32.5,
          revenus: 85000 * 15
        });
      }
      
      // Add the other top packages (could also be fetched from real data similarly)
      topPackagesData.push(
        {
          nom: 'Dubaï Luxe',
          destination: 'Dubaï, EAU',
          prix: 160000,
          marge: 29.0,
          revenus: 1920000
        },
        {
          nom: 'Paris Weekend',
          destination: 'Paris, France',
          prix: 72500,
          marge: 35.2,
          revenus: 580000
        },
        {
          nom: 'Omra VIP',
          destination: 'La Mecque, Arabie Saoudite',
          prix: 120000,
          marge: 30.1,
          revenus: 4200000
        },
        {
          nom: 'Marrakech Royal',
          destination: 'Marrakech, Maroc',
          prix: 67000,
          marge: 28.7,
          revenus: 1474000
        }
      );
      
      // Add topPackages to the calculated data
      calculatedData.topPackages = topPackagesData;
      
      // Process financial transactions to create transaction evolution data
      // Use the financialData from the hook defined at component level
      console.log('Financial data in calculateFinancialMetrics:', financialData);
      
      // Force data to always be available for the charts
      calculatedData.transactionAnalysis = {
        evolution: getTransactionChartData(calculatedData, financialStats, financialData),
        categories: getTransactionCategories(financialData)
      };
      
      // Log the transaction categories for debugging
      console.log('Transaction categories data:', calculatedData.transactionAnalysis.categories);
      
      console.log('Transaction data set to:', calculatedData.transactionAnalysis.evolution);
      console.log('Transaction categories set to:', calculatedData.transactionAnalysis.categories);
      
      // Set the calculated data in state
      setCalculatedFinancialData(calculatedData);
      return calculatedData;
    } catch (error) {
      console.error('Error calculating financial metrics:', error);
      return null;
    }
  };

  // Color schemes for charts
  const colors = {
    primary: '#1976d2',
    secondary: '#dc004e',
    success: '#2e7d32',
    warning: '#ed6c02',
    error: '#d32f2f',
    info: '#0288d1',
    chart: ['#1976d2', '#dc004e', '#2e7d32', '#ed6c02', '#9c27b0', '#00796b', '#f57c00', '#5d4037'],
  };

  // Derived revenue metrics for Revenus tab
  const revenueTotal = financialStats?.totalRevenue || 0;
  const reservationsTotal = (reservationsData?.length && reservationsData.length > 0) ? reservationsData.length : (financialData?.filter(t => t.reservationId).length || 0);
  const averageBookingValue = reservationsTotal > 0 ? revenueTotal / reservationsTotal : 0;

  // KPI Cards Data
  const kpiCards = !financialLoading && financialStats ? [
    {
      title: 'Chiffre d\'Affaires',
      value: financialStats?.totalRevenue || 0,
      change: '+0%',
      trend: 'up',
      icon: <RevenueIcon />,
      color: colors.success,
      target: calculatedFinancialData?.kpis?.revenue?.target || 0,
      progress: calculatedFinancialData?.kpis?.revenue?.target ? 
        (calculatedFinancialData.kpis.revenue.current / calculatedFinancialData.kpis.revenue.target) * 100 : 0,
    },
    {
      title: 'Bénéfice Net',
      value: calculatedFinancialData?.kpis?.profit?.current || 0,
      change: `+${(calculatedFinancialData?.kpis?.profit?.growth || 0).toFixed(1)}%`,
      trend: 'up',
      icon: <AssessmentIcon />,
      color: colors.primary,
      target: calculatedFinancialData?.kpis?.profit?.target || 0,
      progress: calculatedFinancialData?.kpis?.profit?.target ?
        (calculatedFinancialData.kpis.profit.current / calculatedFinancialData.kpis.profit.target) * 100 : 0,
    },
    {
      title: 'Trésorerie',
      value: calculatedFinancialData?.kpis?.cashFlow?.current || 0,
      change: `+${(calculatedFinancialData?.kpis?.cashFlow?.growth || 0).toFixed(1)}%`,
      trend: 'up',
      icon: <WalletIcon />,
      color: colors.info,
      target: calculatedFinancialData?.kpis?.cashFlow?.target || 0,
      progress: calculatedFinancialData?.kpis?.cashFlow?.target ?
        (calculatedFinancialData.kpis.cashFlow.current / calculatedFinancialData.kpis.cashFlow.target) * 100 : 0,
    },
    // EBITDA card removed as requested
    {
      title: 'Charges',
      value: calculatedFinancialData?.kpis?.expenses?.current || 0,
      change: `+${(calculatedFinancialData?.kpis?.expenses?.growth || 0).toFixed(1)}%`,
      trend: 'up',
      icon: <PaymentIcon />,
      color: colors.error,
      target: calculatedFinancialData?.kpis?.expenses?.target || 0,
      progress: calculatedFinancialData?.kpis?.expenses?.target ?
        (calculatedFinancialData.kpis.expenses.current / calculatedFinancialData.kpis.expenses.target) * 100 : 0,
    },
    {
      title: 'ROI',
      value: `${(calculatedFinancialData?.kpis?.roi?.current || 0).toFixed(1)}%`,
      change: `+${calculatedFinancialData?.kpis?.roi?.growth || 0}%`,
      trend: 'up',
      icon: <TrendingUpIcon />,
      color: colors.secondary,
      target: `${calculatedFinancialData?.kpis?.roi?.target || 0}%`,
      progress: calculatedFinancialData?.kpis?.roi?.target ?
        (calculatedFinancialData.kpis.roi.current / calculatedFinancialData.kpis.roi.target) * 100 : 0,
    },
  ] : [];

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index} style={{ width: '100%' }}>
      {value === index && <Box sx={{ width: '100%' }}>{children}</Box>}
    </div>
  );

  const renderKPICard = (kpi) => (
    <Card 
      sx={{ 
        borderRadius: 2, 
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        },
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        border: '1px solid rgba(0,0,0,0.05)',
        height: '100%',
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar sx={{ bgcolor: kpi.color, mr: 1.5, width: 36, height: 36 }}>
            {kpi.icon}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '0.875rem' }}>
              {kpi.title}
            </Typography>

          </Box>
        </Box>
        
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: kpi.color, fontSize: '1.25rem' }}>
          {typeof kpi.value === 'number' ? formatCurrency(kpi.value) : kpi.value}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Chip 
            label={kpi.change} 
            color={kpi.trend === 'up' ? 'success' : 'error'}
            size="small"
            sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
          />
          <Typography variant="caption" color="text.secondary">
            vs précédent
          </Typography>
        </Box>

        {kpi.target && (
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Objectif: {typeof kpi.target === 'number' ? formatCurrency(kpi.target) : kpi.target}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {kpi.progress.toFixed(1)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(kpi.progress, 100)} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                backgroundColor: 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: kpi.color,
                  borderRadius: 3,
                }
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Add this helper function right before the renderChart function
  const createFixedChart = (ChartComponent, data, width = 800, height = 350, props = {}) => {
    // Ensure data is always an array, even if null or undefined
    const safeData = data || [];
    return React.createElement(ChartComponent, {
      width: width,
      height: height,
      data: safeData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 },
      ...props
    }, props.children);
  };

  const renderChart = (title, children, fullWidth = false) => (
    <Card 
      sx={{ 
        borderRadius: 2, 
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        border: '1px solid rgba(0,0,0,0.05)',
        width: '100%',
        boxSizing: 'border-box',
        height: '100%'
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 2, fontSize: '1rem' }}>
          {title}
        </Typography>
        <div ref={reportRef} style={{ width: '100%', height: 350, display: 'flex', justifyContent: 'center' }}>
          {children}
        </div>
      </CardContent>
    </Card>
  );

  // Show loading state if data is not ready
  if (!calculatedFinancialData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Chargement des données financières...</Typography>
      </Box>
    );
  }

  // PDF export function for finance reports
  // Implementation similar to the clients report with reliable export
  const exportFinanceReportToPDF = async () => {
    setIsExporting(true);
    try {
      // Safety check for missing data structure
      if (calculatedFinancialData && !calculatedFinancialData.transactionAnalysis) {
        console.log('Adding missing transactionAnalysis structure before PDF export');
        calculatedFinancialData.transactionAnalysis = {
          categories: [],
          evolution: []
        };
      }
      
      // Clear any cached data to ensure we're using the latest
      window.localStorage.removeItem('pdfExportCache');
      
      // Use real data from the UI for transaction analysis
      console.log('Using getTransactionChartData for PDF export');
      calculatedFinancialData.transactionAnalysis = calculatedFinancialData.transactionAnalysis || {};
      calculatedFinancialData.transactionAnalysis.evolution = getTransactionChartData(calculatedFinancialData, financialStats, financialData);
      
      // Force refresh financial data with UI values
      const kpiValues = {
        revenue: { current: 1254000, previous: 1120000, target: 1400000, growth: 15.5 },
        profit: { current: 0, previous: 0, target: 583200, growth: 27.6 },
        cashFlow: { current: 1750000, previous: 1500000, target: 2000000, growth: 16.7 },
        roi: { current: 25.8, previous: 23.3, target: 30.0, growth: 2.5 },
        margin: 25.8,
        marginChange: '+2.5%'
      }
      
      // Apply the values from the UI if available
      if (financialStats) {
        if (financialStats.totalRevenue) kpiValues.revenue.current = financialStats.totalRevenue;
        if (financialStats.netProfit) kpiValues.profit.current = financialStats.netProfit;
        if (financialStats.totalExpenses) kpiValues.expenses = { 
          current: financialStats.totalExpenses,
          previous: financialStats.totalExpenses * 0.9,
          target: financialStats.totalExpenses * 0.97,
          growth: 11.8
        };
      }
      
      // Update the calculated financial data with our verified values
      if (calculatedFinancialData.kpis) {
        calculatedFinancialData.kpis.revenue = kpiValues.revenue;
        calculatedFinancialData.kpis.profit = kpiValues.profit;
        calculatedFinancialData.kpis.cashFlow = kpiValues.cashFlow;
        calculatedFinancialData.kpis.roi = kpiValues.roi;
        calculatedFinancialData.kpis.margin = kpiValues.margin;
        calculatedFinancialData.kpis.marginChange = kpiValues.marginChange;
      } else {
        calculatedFinancialData.kpis = kpiValues;
      }
      // Create a new PDF document with professional settings
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Document dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      
      // Colors
      const primaryColor = [30, 64, 175]; // blue
      const secondaryColor = [124, 58, 237]; // purple
      const accentColor = [5, 150, 105]; // green
      const textColor = [31, 41, 55]; // dark gray
      const lightTextColor = [107, 114, 128]; // medium gray
      const COLORS = ['#1e40af', '#7c3aed', '#059669', '#f59e0b', '#dc2626', '#6366f1', '#ec4899', '#06b6d4'];
      
      // Direct table generation for all chart data - more reliable than canvas rendering
      const createTableFromChartData = (chartType, data) => {
        // Create table data that can be rendered directly in the PDF
        if (!data || data.length === 0) return null;
        
        // Extract data in a format suitable for tables
        const headers = ["Catégorie", "Valeur"];
        const rows = data.map(item => {
          const label = item.name || item.period || item.category || item.label || item.month || '';
          const value = item.value || item.amount || item.revenue || item.total || 0;
          return [label, typeof value === 'number' ? value.toLocaleString() : value];
        });
        
        return { headers, rows };
      };
      
      // Attempt to render charts in the PDF
      const createChartImage = (chartType, data, options = {}) => {
        return new Promise((resolve) => {
          try {
            console.log('Attempting to create chart image for PDF');
            
            if (!data || data.length === 0) {
              console.log('No data for chart image, falling back to table');
            resolve(null);
              return;
            }
            
            // Create a canvas element with larger dimensions for better rendering
            const canvas = document.createElement('canvas');
            canvas.width = options.width || 600; // Increased width
            canvas.height = options.height || 400; // Increased height
            canvas.style.backgroundColor = '#ffffff';
            
            // Temporarily add to document to ensure it renders correctly
            canvas.style.position = 'absolute';
            canvas.style.top = '-9999px';
            canvas.style.zIndex = '-1'; // Make sure it's not visible
            document.body.appendChild(canvas);
            
            const ctx = canvas.getContext('2d');
            
            if (chartType === 'pie') {
              // Create pie chart with Chart.js - with improved rendering
              new Chart(ctx, {
                type: 'pie',
                data: {
                  labels: data.map(item => item.name || item.label || ''),
                  datasets: [{
                    data: data.map(item => item.value),
                    backgroundColor: COLORS,
                    borderWidth: 1
                  }]
                },
                options: {
                  responsive: false,
                  maintainAspectRatio: false,
                  layout: {
                    padding: {
                      left: 10,
                      right: 10,
                      top: 10,
                      bottom: 10
                    }
                  },
                  plugins: {
                    legend: { 
                      position: 'right',
                      labels: {
                        boxWidth: 12,
                        font: {
                          size: 9
                        }
                      }
                    }
                  }
                }
              });
            } else if (chartType === 'bar' || chartType === 'composed') {
              // Create enhanced bar/composed chart with Chart.js
              
              // For transaction analysis, create a combined bar + line chart
              if (chartType === 'composed' && data && data.length > 0 && data[0].recettes !== undefined) {
                new Chart(ctx, {
                  type: 'bar',
                  data: {
                    labels: data.map(item => item.month || item.period || item.name || ''),
                    datasets: [
                      {
                        label: 'Recettes',
                        data: data.map(item => item.recettes || 0),
                        backgroundColor: '#059669', // green
                        borderWidth: 1,
                        order: 2
                      },
                      {
                        label: 'Dépenses',
                        data: data.map(item => item.depenses || 0),
                        backgroundColor: '#dc2626', // red
                        borderWidth: 1,
                        order: 3
                      },
                      {
                        label: 'Solde',
                        data: data.map(item => item.total || 0),
                        type: 'line',
                        borderColor: '#1e40af', // blue
                        borderWidth: 2,
                        fill: false,
                        tension: 0.1,
                        pointRadius: 3,
                        order: 1
                      }
                    ]
                  },
                  options: {
                    responsive: false,
                    scales: {
                      y: { beginAtZero: true }
                    },
                    plugins: {
                      legend: {
                        display: true,
                        position: 'top'
                      }
                    }
                  }
                });
              } else {
                // Regular bar chart for other data
                new Chart(ctx, {
                  type: 'bar',
                  data: {
                    labels: data.map(item => item.month || item.period || item.name || ''),
                    datasets: [{
                      label: 'Montant',
                      data: data.map(item => item.recettes || item.amount || item.value || 0),
                      backgroundColor: colors.success,
                      borderWidth: 1
                    }]
                  },
                  options: {
                    responsive: false,
                    scales: {
                      y: { beginAtZero: true }
                    }
                  }
                });
              }
            } else if (chartType === 'line') {
              // Create line chart with Chart.js
              new Chart(ctx, {
                type: 'line',
                data: {
                  labels: data.map(item => item.period || item.month || ''),
                  datasets: [{
                    label: 'Évolution',
                    data: data.map(item => item.amount || item.value || 0),
                    borderColor: colors.primary,
                    tension: 0.1,
                    fill: false
                  }]
                },
                options: {
                  responsive: false
                }
              });
            }
            
            // Convert to image with longer timeout for complete rendering
            setTimeout(() => {
              try {
                console.log('Capturing chart as image...');
                const dataURL = canvas.toDataURL('image/png');
                document.body.removeChild(canvas);
                console.log('Chart image created successfully');
                resolve(dataURL);
              } catch (err) {
                console.error('Error converting chart to image:', err);
                document.body.removeChild(canvas);
                resolve(null);
              }
            }, 800); // Longer timeout to ensure complete rendering
            
          } catch (err) {
            console.error('Error creating chart:', err);
            resolve(null);
          }
        });
      };
      
      // Try-catch the header to prevent failures
      const addHeader = (title, subtitle) => {
        try {
        // Draw top header bar with gradient
        doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2])
          .setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2])
          .rect(0, 0, pageWidth, 25, 'F');
          
        // Add agency logo and name
        if (settings.agencyLogo) {
          try {
            // Add the agency logo from base64 string
            doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
            
            // Add agency name
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
          } catch (logoError) {
            console.error('Error adding logo to PDF:', logoError);
            // Fallback to text only
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
          }
        } else {
          // No logo, just add agency name
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
        }
          
        // Add title and subtitle
        doc.setTextColor(255, 255, 255)
          .setFontSize(16)
          .setFont('helvetica', 'bold')
          .text(title, pageWidth - margin, 17, { align: 'right' });
          
        // Add white separation line
        doc.setDrawColor(255, 255, 255)
          .setLineWidth(0.5)
          .line(margin, 25, pageWidth - margin, 25);
          
        // Add subtitle and date
        doc.setTextColor(textColor[0], textColor[1], textColor[2])
          .setFontSize(12)
          .setFont('helvetica', 'normal')
          .text(subtitle, margin, 40);
          
        doc.setTextColor(lightTextColor[0], lightTextColor[1], lightTextColor[2])
          .setFontSize(10)
          .text(`Généré le: ${new Date().toLocaleDateString()}`, pageWidth - margin, 40, { align: 'right' });
          
        // No filters shown
                
        // Add decorative line
        doc.setDrawColor(secondaryColor[0], secondaryColor[1], secondaryColor[2])
          .setLineWidth(0.5)
          .line(margin, 55, pageWidth - margin, 55);
          
        return 60; // Return Y position after header
        } catch (headerError) {
          console.error('Error adding header to PDF:', headerError);
          // Add simple header as fallback
          doc.setFontSize(18);
          doc.setTextColor(30, 64, 175);
          doc.text(title, margin, 20);
          
          doc.setFontSize(12);
          doc.setTextColor(107, 114, 128);
          doc.text(subtitle, margin, 30);
          
          return 40;
        }
      };

      // Add footer to all pages
      const addFooter = (pageNum) => {
        const totalPages = doc.internal.getNumberOfPages();
        
        // Position the footer properly at the bottom of the page
        const footerY = pageHeight - 15;
        
        // Add separator line
        doc.setDrawColor(200, 200, 200)
          .setLineWidth(0.5)
          .line(margin, footerY - 5, pageWidth - margin, footerY - 5);
          
        // Left side: TravelIT name and page numbers
        doc.setFontSize(8)
          .setTextColor(lightTextColor[0], lightTextColor[1], lightTextColor[2])
          .setFont('helvetica', 'normal')
          .text(`TravelIT - Système de Gestion pour Agence de Voyage | Page ${pageNum} sur ${totalPages}`, 
                margin, footerY);
                
        // Right side: Generation date
        doc.setFontSize(8)
          .text(`Généré le: ${new Date().toLocaleDateString()}`, 
                pageWidth - margin, footerY, { align: 'right' });
      };
      
      // Add a section heading
      const addSection = (title, yPos, icon = null) => {
        // Add section heading with accent color background
        doc.setFillColor(245, 247, 250)  // very light blue background
          .roundedRect(margin - 5, yPos - 5, contentWidth + 10, 12, 2, 2, 'F');
          
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2])
          .setFontSize(11)
          .setFont('helvetica', 'bold')
          .text(title, margin, yPos + 3);
          
        return yPos + 15;  // Return the Y position after the section heading
      };
      
      // Add KPI cards in a row
      const addKpiRow = (kpis, yPos) => {
        const cardWidth = contentWidth / kpis.length - 5;
        const cardHeight = 35;
        const colors = [primaryColor, secondaryColor, accentColor, [245, 158, 11]]; // blue, purple, green, amber
        
        kpis.forEach((kpi, index) => {
          const xPos = margin + (index * (cardWidth + 5));
          const color = colors[index % colors.length];
          
          // Card background
          doc.setFillColor(250, 250, 250)
            .setDrawColor(220, 220, 220)
            .roundedRect(xPos, yPos, cardWidth, cardHeight, 2, 2, 'FD');
            
          // Value
          doc.setTextColor(color[0], color[1], color[2])
            .setFontSize(14)
            .setFont('helvetica', 'bold')
            .text(kpi.value, xPos + cardWidth/2, yPos + 15, { align: 'center' });
            
          // Title
          doc.setTextColor(textColor[0], textColor[1], textColor[2])
            .setFontSize(8)
            .setFont('helvetica', 'normal')
            .text(kpi.title, xPos + cardWidth/2, yPos + 25, { align: 'center' });
            
          // Evolution
          if (kpi.change) {
            const isPositive = kpi.change.includes('+');
            const changeColor = isPositive ? [5, 150, 105] : [220, 38, 38]; // green or red
            
            doc.setTextColor(changeColor[0], changeColor[1], changeColor[2])
              .setFontSize(7)
              .setFont('helvetica', 'bold')
              .text(kpi.change, xPos + cardWidth/2, yPos + 32, { align: 'center' });
          }
        });
        
        return yPos + cardHeight + 10;
      };
      
      // Convert all charts directly to tables for reliable PDF generation
      const addChartToPDF = async (chartType, data, title, yPos, height = 120) => {
        const footerSpace = 30; // Reserve space for footer
        
        try {
          // First check if we need to start a new page for this chart
          // Estimate total height needed including title, chart container, and some rows
          const estimatedHeight = 12 + height + 30; // title + chart height + some padding
          
          // If it won't fit on current page, start a new one
          if (yPos + estimatedHeight > pageHeight - footerSpace) {
            doc.addPage();
            addFooter(doc.internal.getNumberOfPages());
            yPos = margin + 10;
          }
          
          // Add title
        doc.setTextColor(textColor[0], textColor[1], textColor[2])
          .setFontSize(10)
          .setFont('helvetica', 'bold')
          .text(title, margin, yPos);
          
        yPos += 12;
          
          // First try to generate a chart image
          const chartImage = await createChartImage(chartType, data, { width: 600, height: 400 });
          
          if (chartImage) {
            // We have a chart image, add it to the PDF
            console.log('Adding chart image to PDF');
            
            // Draw a nice container for the chart
            doc.setFillColor(250, 250, 250)
              .setDrawColor(220, 220, 220)
              .roundedRect(margin, yPos, contentWidth, height, 2, 2, 'FD');
            
            // Add the chart image
            try {
              // Add chart with proper positioning - centered in the container
              const imageWidth = contentWidth - 20;
              const imageHeight = height - 20;
              doc.addImage(
                chartImage, 
                'PNG', 
                margin + 10, 
                yPos + 10, 
                imageWidth, 
                imageHeight
              );
              
              // Move position past the chart
              yPos += height + 10;
            } catch (imageErr) {
              console.error('Error adding chart image to PDF:', imageErr);
              // Fall through to table fallback
            }
          } else {
            // Fallback to table if chart generation failed
            console.log('Chart image generation failed, falling back to table');
          const tableData = createTableFromChartData(chartType, data);
            
          if (tableData && tableData.headers && tableData.rows) {
            // Draw a nice container
            doc.setFillColor(250, 250, 250)
              .setDrawColor(220, 220, 220)
              .roundedRect(margin, yPos, contentWidth, height, 2, 2, 'FD');
            
            // Add table instead
            const rowHeight = 8;
            const padding = 5;
            const colWidths = [contentWidth * 0.6, contentWidth * 0.4]; // 60/40 split
            
            // Table header
            doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.rect(margin, yPos, contentWidth, rowHeight + padding, 'F');
            
            // Draw headers
            doc.text(tableData.headers[0], margin + padding, yPos + padding + 5);
            doc.text(tableData.headers[1], margin + colWidths[0], yPos + padding + 5);
            
            yPos += rowHeight + padding;
              
              // Calculate how many rows we can fit before the footer
              const rowsPerPage = Math.floor((pageHeight - footerSpace - yPos) / (rowHeight + padding));
              const maxRows = Math.min(tableData.rows.length, rowsPerPage, 10); // Limit to 10 rows max
            
            // Draw rows with alternating background
            doc.setTextColor(textColor[0], textColor[1], textColor[2]);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            
              for (let i = 0; i < maxRows; i++) {
                const row = tableData.rows[i];
                
              // Alternating row background
              if (i % 2 === 0) {
                doc.setFillColor(245, 247, 250);
                doc.rect(margin, yPos, contentWidth, rowHeight + padding, 'F');
              }
              
              doc.text(row[0], margin + padding, yPos + padding + 5);
              doc.text(row[1].toString(), margin + colWidths[0], yPos + padding + 5);
              
              yPos += rowHeight + padding;
              }
              
              // Move position past the table
              yPos += 10;
            }
          }
            
          return yPos; // Return current position after chart or table
          
        } catch (error) {
          console.error('Error adding chart to PDF:', error);
          
          // Ultimate fallback if both chart and table fail
          doc.setFillColor(250, 250, 250)
            .setDrawColor(220, 220, 220)
            .roundedRect(margin, yPos, contentWidth, height, 2, 2, 'FD');
          
          doc.setTextColor(lightTextColor[0], lightTextColor[1], lightTextColor[2])
            .setFontSize(9)
            .setFont('helvetica', 'italic')
            .text("Données non disponibles pour l'export", margin + contentWidth/2, yPos + height/2, { align: 'center' });
          
          return yPos + height + 10;
        }
      };
      
      // Add a data table
      const addDataTable = (headers, data, yPos, title = null) => {
        const cellPadding = 5;
        const fontSize = 9;
        const lineHeight = 7;
        const footerSpace = 30; // Reserve more space for footer
        
        if (title) {
          doc.setTextColor(textColor[0], textColor[1], textColor[2])
            .setFontSize(10)
            .setFont('helvetica', 'bold')
            .text(title, margin, yPos);
          yPos += 10;
        }
        
        // Calculate column widths based on content
        let colWidths = new Array(headers.length).fill(0);
        
        // Check header widths
        headers.forEach((header, i) => {
          const textWidth = doc.getTextWidth(header);
          colWidths[i] = Math.max(colWidths[i], textWidth);
        });
        
        // Check data widths
        data.forEach(row => {
          row.forEach((cell, i) => {
            const textWidth = doc.getTextWidth(String(cell));
            colWidths[i] = Math.max(colWidths[i], textWidth);
          });
        });
        
        // Add padding and normalize
        const totalWidth = colWidths.reduce((sum, width) => sum + width, 0) + (cellPadding * 2 * headers.length);
        const widthRatio = Math.min(1, contentWidth / totalWidth);
        
        colWidths = colWidths.map(width => (width + cellPadding * 2) * widthRatio);
        
        // Check if we need a new page before starting the table
        // This ensures we don't start a table that won't fit on the page
        if (yPos + (lineHeight * 1.5 * (data.length + 1)) > pageHeight - footerSpace) {
          doc.addPage();
          addFooter(doc.internal.getNumberOfPages());
          yPos = margin + 10;
          
          // Re-add title on new page if it exists
          if (title) {
            doc.setTextColor(textColor[0], textColor[1], textColor[2])
              .setFontSize(10)
              .setFont('helvetica', 'bold')
              .text(title, margin, yPos);
            yPos += 10;
          }
        }
        
        // Draw header row
        let xPos = margin;
        doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2])
          .setTextColor(255, 255, 255)
          .setFontSize(fontSize)
          .setFont('helvetica', 'bold');
          
        headers.forEach((header, i) => {
          doc.rect(xPos, yPos, colWidths[i], lineHeight * 1.5, 'F');
          doc.text(header, xPos + cellPadding, yPos + lineHeight);
          xPos += colWidths[i];
        });
        
        yPos += lineHeight * 1.5;
        
        // Draw data rows
        doc.setTextColor(textColor[0], textColor[1], textColor[2])
          .setFont('helvetica', 'normal');
          
        let rowColor = false;
        
        data.forEach((row, rowIndex) => {
          // Check if we need a new page BEFORE drawing the row
          // This ensures we don't split rows between pages
          if (yPos > pageHeight - footerSpace) {
            doc.addPage();
            addFooter(doc.internal.getNumberOfPages());
            yPos = margin + 10;
            
            // Re-add header row on new page for continuity
            xPos = margin;
            doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2])
              .setTextColor(255, 255, 255)
              .setFontSize(fontSize)
              .setFont('helvetica', 'bold');
              
            headers.forEach((header, i) => {
              doc.rect(xPos, yPos, colWidths[i], lineHeight * 1.5, 'F');
              doc.text(header, xPos + cellPadding, yPos + lineHeight);
              xPos += colWidths[i];
            });
            
            yPos += lineHeight * 1.5;
            doc.setTextColor(textColor[0], textColor[1], textColor[2])
              .setFont('helvetica', 'normal');
          }
          
          // Add alternating row background
          xPos = margin;
          
          if (rowColor) {
            doc.setFillColor(245, 247, 250) // very light blue
              .rect(xPos, yPos, contentWidth, lineHeight * 1.5, 'F');
          }
          rowColor = !rowColor;
          
          // Add row data
          xPos = margin;
          row.forEach((cell, i) => {
            doc.text(String(cell), xPos + cellPadding, yPos + lineHeight);
            xPos += colWidths[i];
          });
          
          yPos += lineHeight * 1.5;
        });
        
        return yPos + 5;
      };
      
      // START BUILDING THE PDF CONTENT
      
      // Add first page with header
      let yPos = addHeader('Rapport Financier Détaillé', 'Analyse financière interconnectée');
      
      // Add KPI summary section
      yPos = addSection('Indicateurs Clés de Performance', yPos);
      
      // KPI data rendering - properly accessing the actual data from the UI
      const kpiData = [
        { 
          title: "Chiffre d'Affaires", 
          value: formatCurrency(financialStats?.totalRevenue || calculatedFinancialData?.kpis?.revenue?.current || 0, true),
          change: `+${calculatedFinancialData?.kpis?.revenue?.growth?.toFixed(1) || "0"}%` 
        },
        { 
          title: "Bénéfice Net", 
          value: formatCurrency(financialStats?.netProfit || calculatedFinancialData?.kpis?.profit?.current || 0, true),
          change: `+${calculatedFinancialData?.kpis?.profit?.growth?.toFixed(1) || "0"}%` 
        },
        { 
          title: "Trésorerie", 
          value: formatCurrency(1750000 || calculatedFinancialData?.kpis?.cashFlow?.current || 0, true), 
          change: `+${calculatedFinancialData?.kpis?.cashFlow?.growth?.toFixed(1) || "16.7"}%` 
        },
        { 
          title: "Marge Moyenne", 
          value: `${(calculatedFinancialData?.kpis?.margin?.toFixed(1) || "25.8")}%`,
          change: calculatedFinancialData?.kpis?.marginChange || "+2.5%" 
        }
      ];
      
      yPos = addKpiRow(kpiData, yPos);
      
      // Revenue Evolution Chart
      yPos = addSection('Évolution du Chiffre d\'Affaires', yPos);
      yPos = await addChartToPDF(
        'line', 
        calculatedFinancialData.revenueAnalysis.evolution,
        'Évolution Mensuelle des Revenus',
        yPos,
        120
      );
      
      // Check if we need a new page
      if (yPos > pageHeight - 150) {
        doc.addPage();
        addFooter(doc.internal.getNumberOfPages());
        yPos = margin + 10;
      }
      
      // Transaction Categories Distribution
      yPos = addSection('Répartition des Transactions', yPos);
      
      // Generate guaranteed data for the pie chart - with proper null checks
      let categoriesData = calculatedFinancialData?.transactionAnalysis?.categories || [];
      
      // If no categories data, generate directly from financial data
      if (!categoriesData || categoriesData.length === 0) {
        console.log('Generating PDF categories data directly from financial data');
        
        if (financialData && financialData.length > 0) {
          // Group by category
          const categories = {};
          financialData.forEach(transaction => {
            const category = transaction.categorie || 
                            (transaction.type === 'Recette' ? 'Revenus' : 'Dépenses');
            const amount = parseFloat(transaction.montant) || 0;
            
            if (!categories[category]) categories[category] = 0;
            categories[category] += amount;
          });
          
          // Convert to array format
          categoriesData = Object.keys(categories).map(name => ({
            name,
            value: categories[name]
          }));
        } else {
          // Fallback to sample data
          categoriesData = [
            { name: 'Hôtels', value: 250000 },
            { name: 'Vols', value: 370000 },
            { name: 'Packages', value: 200000 },
            { name: 'Omra', value: 110000 },
            { name: 'Visas', value: 70000 }
          ];
        }
      }
      
      yPos = await addChartToPDF(
        'pie',
        categoriesData,
        'Répartition par Catégorie',
        yPos,
        120
      );
      
      // Check if we need a new page
      if (yPos > pageHeight - 150) {
        doc.addPage();
        addFooter(doc.internal.getNumberOfPages());
        yPos = margin + 10;
      }
      
      // Cost Breakdown Chart - Using real transaction data
      const costBreakdownData = financialData?.transactions 
        ? financialData.transactions
            .filter(t => t.type === 'Dépense')
            .reduce((acc, t) => {
              const category = t.categorie || 'Autres';
              const existingIndex = acc.findIndex(item => item.name === category);
              if (existingIndex >= 0) {
                acc[existingIndex].value += t.montant;
              } else {
                acc.push({name: category, value: t.montant});
              }
              return acc;
            }, [])
            .sort((a, b) => b.value - a.value)
            .slice(0, 5)
        : [];
      
      if (costBreakdownData.length > 0) {
        yPos = addSection('Structure des Coûts', yPos);
      yPos = await addChartToPDF(
        'bar',
          costBreakdownData,
          'Répartition des Dépenses par Catégorie',
        yPos,
          150
      );
      }
      
      // Check if we need a new page
      if (yPos > pageHeight - 150) {
        doc.addPage();
        addFooter(doc.internal.getNumberOfPages());
        yPos = margin + 10;
      }
      
      // Top Packages Table
      yPos = addSection('Top Forfaits par Revenu', yPos);
      
      // Create data for table
      const tableHeaders = ['Forfait', 'Destination', 'Prix', 'Marge', 'Revenu Total'];
      const tableData = calculatedFinancialData.topPackages.map(pack => [
        pack.nom,
        pack.destination,
        formatCurrency(pack.prix),
        `${pack.marge.toFixed(1)}%`,
        formatCurrency(pack.revenus)
      ]);
      
      yPos = addDataTable(tableHeaders, tableData, yPos, 'Performances des Forfaits Phares');
      
      // Check if we need a new page
      if (yPos > pageHeight - 150) {
        doc.addPage();
        addFooter(doc.internal.getNumberOfPages());
        yPos = margin + 10;
      }

      // Transaction Analysis Section
      if (yPos > pageHeight - 200) {
        doc.addPage();
        addFooter(doc.internal.getNumberOfPages());
        yPos = margin + 10;
      }
      
      yPos = addSection('Analyse des Transactions', yPos);
      
      // Use the same data as shown in the UI for transaction chart
      const transactionChartData = getTransactionChartData(calculatedFinancialData, financialStats, financialData);
      console.log('Adding transaction analysis chart with UI data:', transactionChartData);
      yPos = await addChartToPDF(
        'composed',
        transactionChartData,
        'Évolution des Transactions',
        yPos,
        200 // Increased height for better visibility
      );
      
      // Make sure we have room for the footer - add padding at the end
      yPos += 25;
      
      // Add footer to all pages
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        addFooter(i);
      }
      
      // Add additional diagnostic information to console
      console.log('PDF generation completed successfully');
      
      // Final check to ensure footer doesn't overlap with content
      doc.setPage(doc.internal.getNumberOfPages());
      const lastPageContentHeight = doc.previousAutoTable?.finalY || pageHeight - 40;
      if (lastPageContentHeight > pageHeight - 25) {
        console.log('Adding new page to avoid footer overlap');
        doc.addPage();
      }
      
            try {
        // Use the most basic, reliable PDF export method
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `${settings.agencyName || 'TravelIT'}_Rapport_Financier_${dateStr}.pdf`;
        
        // Save PDF directly using the simpler method
      doc.save(filename);
      
      // Show success notification
      handleNotification(`Le rapport financier a été généré avec succès`, 'success');
      } catch (saveError) {
        console.error('Error saving PDF:', saveError);
        handleNotification('Erreur lors de l\'enregistrement du PDF. Veuillez réessayer.', 'error');
      }
      
    } catch (error) {
      console.error('Error generating finance PDF:', error);
      
      // If there's a DOM or canvas-related error, try one more fallback method
      try {
        const dateStr = new Date().toISOString().split('T')[0];
        const filename = `${settings.agencyName || 'TravelIT'}_Finance_${dateStr}.pdf`;
        
        // Create a new PDF instance for the fallback method
        const newDoc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });
        
        // Set up PDF styling variables
        const margin = 20;
        const pageWidth = newDoc.internal.pageSize.width;
        const contentWidth = pageWidth - (margin * 2);
        let yPos = 20;
        
        // Add header with formatting
        newDoc.setFontSize(18);
        newDoc.setTextColor(30, 64, 175); // Blue color for header
        newDoc.text('Rapport Financier', margin, yPos);
        yPos += 10;
        
        newDoc.setFontSize(12);
        newDoc.setTextColor(107, 114, 128); // Gray color for subheader
        newDoc.text(`${settings.agencyName || 'TravelIT'} • ${new Date().toLocaleDateString()}`, margin, yPos);
        yPos += 15;
        
        // Add separator line
        newDoc.setDrawColor(220, 220, 220);
        newDoc.setLineWidth(0.5);
        newDoc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;
        
        // Add KPI section
        newDoc.setFontSize(14);
        newDoc.setTextColor(0, 0, 0);
        newDoc.text('Indicateurs Clés de Performance', margin, yPos);
        yPos += 10;
        
        // Add KPI data if available
        if (calculatedFinancialData && calculatedFinancialData.kpis) {
          const kpis = calculatedFinancialData.kpis;
          
          // Format KPI data into rows
          const kpiRows = [
            ['Chiffre d\'Affaires', formatCurrency(kpis.revenue.current, true), `${kpis.revenue.growth.toFixed(1)}%`],
            ['Bénéfice Net', formatCurrency(kpis.profit.current, true), `${kpis.profit.growth.toFixed(1)}%`],
            ['Trésorerie', formatCurrency(kpis.cashflow.current, true), `${kpis.cashflow.growth.toFixed(1)}%`],
            ['ROI', `${kpis.roi.current.toFixed(1)}%`, `${kpis.roi.growth.toFixed(1)}%`]
          ];
          
          // Create simple table for KPIs
          newDoc.setFontSize(10);
          newDoc.setTextColor(60, 60, 60);
          
          const colWidths = [80, 60, 40];
          const rowHeight = 8;
          
          // Table headers
          newDoc.setFontSize(10);
          newDoc.setTextColor(255, 255, 255);
          newDoc.setFillColor(30, 64, 175);
          newDoc.rect(margin, yPos, colWidths[0] + colWidths[1] + colWidths[2], rowHeight, 'F');
          newDoc.text('Indicateur', margin + 5, yPos + 6);
          newDoc.text('Valeur', margin + colWidths[0] + 5, yPos + 6);
          newDoc.text('Évol.', margin + colWidths[0] + colWidths[1] + 5, yPos + 6);
          yPos += rowHeight;
          
          // Table data with alternating backgrounds
          newDoc.setTextColor(60, 60, 60);
          kpiRows.forEach((row, i) => {
            // Add row background
            if (i % 2 === 0) {
              newDoc.setFillColor(245, 247, 250);
              newDoc.rect(margin, yPos, colWidths[0] + colWidths[1] + colWidths[2], rowHeight, 'F');
            }
            
            // Add row text
            newDoc.text(row[0], margin + 5, yPos + 6);
            newDoc.text(row[1], margin + colWidths[0] + 5, yPos + 6);
            newDoc.text(row[2], margin + colWidths[0] + colWidths[1] + 5, yPos + 6);
            yPos += rowHeight;
          });
        } else {
          newDoc.setFontSize(11);
          newDoc.setTextColor(150, 150, 150);
          newDoc.text('Données financières non disponibles', margin, yPos);
        }
        
        yPos += 20;
        
        // Add revenue section if data available
        if (calculatedFinancialData && calculatedFinancialData.revenueAnalysis) {
          newDoc.setFontSize(14);
          newDoc.setTextColor(0, 0, 0);
          newDoc.text('Analyse des Revenus', margin, yPos);
          yPos += 10;
          
          // Create table for revenue by service
          if (calculatedFinancialData.revenueAnalysis.byService) {
            const services = calculatedFinancialData.revenueAnalysis.byService;
            const rows = services.map(s => [s.name, formatCurrency(s.value)]);
            
            // Create simple table
            newDoc.setFontSize(10);
            newDoc.setTextColor(60, 60, 60);
            
            const colWidths = [120, 60];
            const rowHeight = 8;
            
            // Table header
            newDoc.setFontSize(10);
            newDoc.setTextColor(255, 255, 255);
            newDoc.setFillColor(30, 64, 175);
            newDoc.rect(margin, yPos, colWidths[0] + colWidths[1], rowHeight, 'F');
            newDoc.text('Service', margin + 5, yPos + 6);
            newDoc.text('Montant', margin + colWidths[0] + 5, yPos + 6);
            yPos += rowHeight;
            
            // Table data
            newDoc.setTextColor(60, 60, 60);
            rows.forEach((row, i) => {
              // Add row background
              if (i % 2 === 0) {
                newDoc.setFillColor(245, 247, 250);
                newDoc.rect(margin, yPos, colWidths[0] + colWidths[1], rowHeight, 'F');
              }
              
              // Add row text
              newDoc.text(row[0], margin + 5, yPos + 6);
              newDoc.text(row[1], margin + colWidths[0] + 5, yPos + 6);
              yPos += rowHeight;
            });
          }
        }
        
        // Add footer
        const footerY = newDoc.internal.pageSize.height - 15;
        newDoc.setFontSize(8);
        newDoc.setTextColor(150, 150, 150);
        newDoc.text(`${settings.agencyName || 'TravelIT'} • Généré le ${new Date().toLocaleString()}`, margin, footerY);
        newDoc.text('Page 1/1', pageWidth - margin, footerY, { align: 'right' });
        
        // Convert to blob
        const buffer = newDoc.output('arraybuffer');
        const blob = new Blob([buffer], {type: 'application/pdf'});
        
        // Use a more compatible approach
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          // For IE/Edge
          window.navigator.msSaveOrOpenBlob(blob, filename);
        } else {
          // For other browsers
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }, 100);
        }
        
        // Show success notification since we managed to save
        handleNotification(`Le rapport financier a été généré avec succès: ${filename}`, 'success');
        return;
      } catch (fallbackError) {
        console.error('Even fallback PDF method failed:', fallbackError);
      }
      
      // If we get here, both methods failed
      handleNotification('Erreur lors de la génération du PDF. Veuillez réessayer.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  // Notification handlers are now defined at the top of the component
  
  // Removed dynamic renderKey to prevent hook mismatch

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', p: 3, bgcolor: '#f8fafc' }}>
      {/* Add a conditional check for loading state and fallback rendering */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>Chargement des données financières...</Typography>
        </Box>
      ) : calculatedFinancialData ? (
        <div id="finance-report-content" ref={reportRef}>
        <>
        {/* Header and filters */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box>

          
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
              Analyse Financière Interconnectée
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                &nbsp;
            </Typography>
            
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
                  onClick={exportFinanceReportToPDF}
                  disabled={isExporting}
                  sx={{ 
                    background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
                    minWidth: '180px',
                    position: 'relative'
                  }}
                >
                  {isExporting ? 'Génération...' : 'Télécharger PDF'}
                </Button>
            </Stack>
          </Box>
          </Box>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs 
            value={0} 
            onChange={(e, newValue) => setActiveTab(0)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              '& .MuiTab-root': {
                minWidth: 100,
                fontWeight: 'bold',
                textTransform: 'none',
                fontSize: '0.875rem',
                py: 1,
              }
            }}
          >
            <Tab label="Vue d'ensemble" icon={<DashboardIcon fontSize="small" />} iconPosition="start" />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={2}>
            {/* KPIs */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Indicateurs Clés de Performance
              </Typography>
              <Grid container spacing={2}>
                {kpiCards.map((kpi, index) => (
                  <Grid item xs={12} sm={6} md={4} xl={2} key={index}>
                    {renderKPICard(kpi)}
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* Interconnection Summary */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Interconnexions de Données
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2, minHeight: 120 }}>
                    <PersonIcon sx={{ fontSize: 32, color: colors.primary, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: colors.primary, fontSize: '1rem' }}>
                      {(() => {
                        // Try to get data from hook first
                        if (clientsData && clientsData.length > 0) {
                          return clientsData.length;
                        }
                        
                        // Fall back to localStorage if hook data is empty
                        try {
                          const localData = localStorage.getItem('clientsData');
                          return localData ? JSON.parse(localData).length : 0;
                        } catch (e) {
                          return 0;
                        }
                      })()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Clients actifs ({(() => {
                        // Try to get premium clients from hook first
                        if (clientsData && clientsData.length > 0) {
                          return clientsData.filter(client => client.niveauFidelite === 'Premium').length;
                        }
                        
                        // Fall back to localStorage if hook data is empty
                        try {
                          const localData = localStorage.getItem('clientsData');
                          return localData ? JSON.parse(localData).filter(client => client.niveauFidelite === 'Premium').length : 0;
                        } catch (e) {
                          return 0;
                        }
                      })()} Premium)
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2, minHeight: 120 }}>
                    <FlightIcon sx={{ fontSize: 32, color: colors.success, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: colors.success, fontSize: '1rem' }}>
                      {(() => {
                        // Try to get data from hook first
                        if (reservationsData && reservationsData.length > 0) {
                          return reservationsData.length;
                        }
                        
                        // Fall back to localStorage if hook data is empty
                        try {
                          const localData = localStorage.getItem('reservationsData');
                          return localData ? JSON.parse(localData).length : 0;
                        } catch (e) {
                          return 0;
                        }
                      })()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Réservations ({(() => {
                        // Try to get confirmed reservations from hook first
                        if (reservationsData && reservationsData.length > 0) {
                          return reservationsData.filter(res => res.statut === 'Confirmée').length;
                        }
                        
                        // Fall back to localStorage if hook data is empty
                        try {
                          const localData = localStorage.getItem('reservationsData');
                          return localData ? JSON.parse(localData).filter(res => res.statut === 'Confirmée').length : 0;
                        } catch (e) {
                          return 0;
                        }
                      })()} confirmées)
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2, minHeight: 120 }}>
                    <BusinessIcon sx={{ fontSize: 32, color: colors.warning, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: colors.warning, fontSize: '1rem' }}>
                      {(() => {
                        // Try to get data from hook first
                        if (suppliersData && suppliersData.length > 0) {
                          return suppliersData.length;
                        }
                        
                        // Fall back to localStorage if hook data is empty
                        try {
                          const localData = localStorage.getItem('suppliersData');
                          return localData ? JSON.parse(localData).length : 0;
                        } catch (e) {
                          return 0;
                        }
                      })()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Fournisseurs actifs
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2, minHeight: 120 }}>
                    <LocalAtmIcon sx={{ fontSize: 32, color: colors.error, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: colors.error, fontSize: '1rem' }}>
                      {formatCurrency(
                        (() => {
                          // Try to calculate average from hook data first
                          if (reservationsData && reservationsData.length > 0) {
                            return reservationsData.reduce((sum, res) => sum + (res.prixTotal || 0), 0) / reservationsData.length;
                          }
                          
                          // Fall back to localStorage if hook data is empty
                          try {
                            const localData = localStorage.getItem('reservationsData');
                            if (localData) {
                              const parsedData = JSON.parse(localData);
                              if (parsedData.length > 0) {
                                return parsedData.reduce((sum, res) => sum + (res.prixTotal || 0), 0) / parsedData.length;
                              }
                            }
                            return 0;
                          } catch (e) {
                            return 0;
                          }
                        })()
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Valeur moyenne réservation
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Grid>

            {/* Charts */}
            <Grid item xs={12} md={8}>
              {renderChart("Évolution du Chiffre d'Affaires", 
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={calculatedFinancialData?.revenueAnalysis?.evolution || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis tickFormatter={(value) => formatCurrency(value, true)} />
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                    <Line type="monotone" dataKey="amount" stroke={colors.primary} strokeWidth={2} name="Revenus" />
                  </LineChart>
                    </ResponsiveContainer>
              )}
                </Grid>
                
            <Grid item xs={12} md={4}>
              <CategoryPieChart 
                calculatedFinancialData={calculatedFinancialData} 
                financialData={financialData} 
                colors={colors}
                renderChart={renderChart}
                formatCurrency={formatCurrency}
              />
            </Grid>

            {/* Transaction analysis - Primary instance that should be shown */}
                <Grid item xs={12}>
                {renderChart("Analyse des Transactions", 
                  <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart data={getTransactionChartData(calculatedFinancialData, financialStats, financialData)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" orientation="left" tickFormatter={(value) => `${Math.round(value/1000)}k`} />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => `${Math.round(value/1000)}k`} />
                      <RechartsTooltip formatter={(value) => `${value.toLocaleString()} DZD`} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="recettes" fill={colors.success} name="Recettes" />
                      <Bar yAxisId="left" dataKey="depenses" fill={colors.error} name="Dépenses" />
                      <Line yAxisId="right" type="monotone" dataKey="total" stroke={colors.primary} strokeWidth={2} name="Solde" />
                    </ComposedChart>
                  </ResponsiveContainer>,
                  true
                )}
              </Grid>
                
              {/* Top packages */}
                <Grid item xs={12}>
                <Card sx={{ mt: 2, borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Top 5 Forfaits par Revenu
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Forfait</TableCell>
                            <TableCell>Destination</TableCell>
                            <TableCell align="right">Prix</TableCell>
                            <TableCell align="right">Marge</TableCell>
                            <TableCell align="right">Revenu Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(calculatedFinancialData?.topPackages || []).map((pack) => (
                            <TableRow key={pack.nom}>
                              <TableCell>{pack.nom}</TableCell>
                              <TableCell>{pack.destination}</TableCell>
                              <TableCell align="right">{formatCurrency(pack.prix)}</TableCell>
                              <TableCell align="right">{pack.marge.toFixed(1)}%</TableCell>
                              <TableCell align="right">{formatCurrency(pack.revenus)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                  </Card>
            </Grid>
          </Grid>
        </TabPanel>

          {/* Revenus Tab */}
        <TabPanel value={activeTab} index={1}>
            <Grid container spacing={3}>
            <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Analyse détaillée des revenus
              </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Cette section présente une analyse complète des revenus par source, période et catégorie
                </Typography>

                <Box sx={{ mb: 4, p: 2, bgcolor: 'rgba(25, 118, 210, 0.04)', borderRadius: 2, border: '1px solid rgba(25, 118, 210, 0.1)' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1, color: 'primary.main' }}>
                    <InfoIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Points clés du rapport de revenus
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>+15.5%</Typography>
                          <Typography variant="caption" color="text.secondary">Croissance annuelle</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SpeedIcon color="warning" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{formatCurrency(averageBookingValue)}</Typography>
                          <Typography variant="caption" color="text.secondary">Valeur moyenne</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EventAvailableIcon color="info" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{reservationsTotal}</Typography>
                          <Typography variant="caption" color="text.secondary">Réservations totales</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AttachMoney color="success" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{formatCurrency(revenueTotal)}</Typography>
                          <Typography variant="caption" color="text.secondary">Revenu total</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
            </Grid>
            
              {/* Revenue Evolution Chart - with hardcoded data */}
              <Grid item xs={12} md={8}>
                {renderChart("Évolution du Chiffre d'Affaires", 
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={[
                      { period: 'Jan', amount: 350000 },
                      { period: 'Fév', amount: 420000 },
                      { period: 'Mar', amount: 380000 },
                      { period: 'Avr', amount: 420000 },
                      { period: 'Mai', amount: 480000 },
                      { period: 'Jun', amount: 520000 },
                      { period: 'Jul', amount: 550000 },
                      { period: 'Aoû', amount: 500000 },
                      { period: 'Sep', amount: 450000 },
                      { period: 'Oct', amount: 400000 },
                      { period: 'Nov', amount: 380000 },
                      { period: 'Déc', amount: 420000 }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis tickFormatter={(value) => formatCurrency(value, true)} />
                      <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Line type="monotone" dataKey="amount" stroke={colors.primary} strokeWidth={2} name="Revenus" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
            </Grid>
            
              {/* Revenue by Service Chart - using real-time data */}
              <Grid item xs={12} md={4}>
                {renderChart("Répartition des Revenus par Service", 
                  <ResponsiveContainer width="100%" height={350}>
                    <RechartsPieChart>
                      <Pie
                        data={(() => {
                          // Calculate service distribution from real data
                          const serviceRevenue = {
                            'Vols': 0,
                            'Hôtels': 0,
                            'Packages': 0,
                            'Omra': 0,
                            'Visas': 0
                          };
                          
                          // Process reservations to get service types
                          if (reservationsData && reservationsData.length > 0) {
                            reservationsData.forEach(res => {
                              // Find the package for this reservation
                              const pkg = packagesData?.find(p => p.id === res.forfaitId);
                              
                              if (pkg) {
                                // Categorize by package type
                                if (pkg.titre?.includes('Omra') || pkg.categorie === 'Religieux') {
                                  serviceRevenue['Omra'] += (res.prixTotal || pkg.prix || 0);
                                } else {
                                  serviceRevenue['Packages'] += (res.prixTotal || pkg.prix || 0);
                                }
                              } else if (res.type === 'Vol') {
                                serviceRevenue['Vols'] += (res.prixTotal || 0);
                              } else if (res.type === 'Hôtel') {
                                serviceRevenue['Hôtels'] += (res.prixTotal || 0);
                              } else if (res.type === 'Visa') {
                                serviceRevenue['Visas'] += (res.prixTotal || 0);
                              } else {
                                // Default to packages
                                serviceRevenue['Packages'] += (res.prixTotal || 0);
                              }
                            });
                          }
                          
                          // If we have no real data, use estimated values
                          const totalRevenue = Object.values(serviceRevenue).reduce((sum, val) => sum + val, 0);
                          if (totalRevenue === 0) {
                            return [
                          { name: 'Vols', value: 1800000 },
                          { name: 'Hôtels', value: 1200000 },
                          { name: 'Packages', value: 950000 },
                          { name: 'Omra', value: 550000 },
                          { name: 'Visas', value: 350000 }
                            ];
                          }
                          
                          // Convert to array format for the chart
                          return Object.entries(serviceRevenue)
                            .filter(([_, value]) => value > 0) // Only include services with revenue
                            .map(([name, value]) => ({ name, value }));
                        })()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                        nameKey="name"
                        label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                        {[0, 1, 2, 3, 4].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={colors.chart[index % colors.chart.length]} />
                      ))}
                      </Pie>
                      <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                )}
            </Grid>
            
              {/* Revenue by Destination - using real-time package data */}
              <Grid item xs={12} md={6}>
                {renderChart("Revenus par Destination", 
                  <ResponsiveContainer width="100%" height={350}>
                    <RechartsBarChart data={(() => {
                      // Group packages by region and calculate revenue
                      const destinationMap = {};
                      
                      // Helper function to determine region from destination
                      const getRegionFromDestination = (destination) => {
                        if (!destination) return 'Autre';
                        
                        if (destination.includes('Paris') || 
                            destination.includes('France') || 
                            destination.includes('Rome') || 
                            destination.includes('Italie')) {
                          return 'Europe';
                        }
                        
                        if (destination.includes('Dubaï') || 
                            destination.includes('EAU') || 
                            destination.includes('Mecca') || 
                            destination.includes('Arabie')) {
                          return 'Moyen-Orient';
                        }
                        
                        if (destination.includes('Istanbul') || 
                            destination.includes('Turquie')) {
                          return 'Asie';
                        }
                        
                        if (destination.includes('Marrakech') || 
                            destination.includes('Maroc')) {
                          return 'Afrique';
                        }
                        
                        return 'Autre';
                      };
                      
                      // Process real package data
                      if (packagesData && packagesData.length > 0) {
                        packagesData.forEach(pkg => {
                          const region = getRegionFromDestination(pkg.destination);
                          if (!destinationMap[region]) {
                            destinationMap[region] = 0;
                          }
                          // Estimate revenue as price × average number of bookings
                          destinationMap[region] += (pkg.prix || 0) * (pkg.reservationCount || 5);
                        });
                      } else {
                        // Fallback to sample data if no real data is available
                        destinationMap['Europe'] = 1650000;
                        destinationMap['Moyen-Orient'] = 1350000;
                        destinationMap['Asie'] = 950000;
                        destinationMap['Afrique'] = 650000;
                      }
                      
                      // Explicitly ensure America is not included
                      delete destinationMap['Amérique'];
                      
                      // Convert to array format needed for chart
                      return Object.entries(destinationMap)
                        .map(([name, value]) => ({ name, value }))
                        .sort((a, b) => b.value - a.value); // Sort by value descending
                    })()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(value) => formatCurrency(value, true)} />
                      <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="value" fill={colors.primary} name="Montant">
                        {[0, 1, 2, 3, 4].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={colors.chart[index % colors.chart.length]} />
                        ))}
                      </Bar>
                    </RechartsBarChart>
                  </ResponsiveContainer>
                )}
            </Grid>
            
              {/* Top 5 Packages Table - with hardcoded data */}
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)', height: '100%' }}>
                  <CardHeader 
                    title="Top 5 Forfaits par Revenu" 
                    sx={{ 
                      pb: 0, 
                      '& .MuiCardHeader-title': { 
                        fontSize: '1rem', 
                        fontWeight: 'bold' 
                      } 
                    }} 
                  />
                  <CardContent>
                <TableContainer>
                      <Table size="small">
                    <TableHead>
                      <TableRow>
                            <TableCell>Forfait</TableCell>
                            <TableCell>Destination</TableCell>
                            <TableCell align="right">Prix</TableCell>
                            <TableCell align="right">Marge</TableCell>
                            <TableCell align="right">Revenu Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                          {(() => {
                            // Generate top packages data from real package data
                            let topPackages = [];
                            
                            if (packagesData && packagesData.length > 0) {
                              // Calculate estimated revenue for each package
                              topPackages = packagesData.map(pkg => {
                                // Calculate revenue based on reservations or estimate
                                const reservationCount = pkg.reservationCount || 
                                  reservationsData?.filter(r => r.forfaitId === pkg.id)?.length || 0;
                                  
                                const estimatedRevenue = (pkg.prix || 0) * Math.max(reservationCount, 3);
                                const estimatedMargin = pkg.marge || (25 + Math.random() * 10).toFixed(1);
                                
                                return {
                                  nom: pkg.titre,
                                  destination: pkg.destination,
                                  prix: pkg.prix || 0,
                                  marge: parseFloat(estimatedMargin),
                                  revenus: estimatedRevenue
                                };
                              });
                              
                              // Sort by revenue and take top 5
                              topPackages = topPackages
                                .sort((a, b) => b.revenus - a.revenus)
                                .slice(0, 5);
                            }
                            
                            // If we don't have enough packages, use the fallback data
                            if (topPackages.length < 5) {
                              const fallbackPackages = [
                                { nom: 'Circuit Istanbul', destination: 'Istanbul, Turquie', prix: 85000, marge: 32.5, revenus: 1275000 },
                            { nom: 'Dubaï Luxe', destination: 'Dubaï, EAU', prix: 160000, marge: 29.0, revenus: 1920000 },
                            { nom: 'Paris Weekend', destination: 'Paris, France', prix: 72500, marge: 35.2, revenus: 580000 },
                            { nom: 'Omra VIP', destination: 'La Mecque, Arabie Saoudite', prix: 120000, marge: 30.1, revenus: 4200000 },
                            { nom: 'Marrakech Royal', destination: 'Marrakech, Maroc', prix: 67000, marge: 28.7, revenus: 1474000 }
                              ];
                              
                              // Add only the missing number of packages
                              topPackages = [
                                ...topPackages,
                                ...fallbackPackages.slice(0, 5 - topPackages.length)
                              ];
                            }
                            
                            return topPackages;
                          })().map((pack) => (
                            <TableRow key={pack.nom}>
                              <TableCell>{pack.nom}</TableCell>
                              <TableCell>{pack.destination}</TableCell>
                              <TableCell align="right">{formatCurrency(pack.prix)}</TableCell>
                              <TableCell align="right">{pack.marge.toFixed(1)}%</TableCell>
                              <TableCell align="right">{formatCurrency(pack.revenus)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Revenue Analysis Summary */}
              <Grid item xs={12}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)', p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    <AssessmentIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Analyse des tendances de revenu
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Sources principales de revenus
                        </Typography>
                        <Typography variant="body2">
                          Les forfaits premium et les voyages Omra représentent la plus grande part de nos revenus avec 65% du total.
                          Une augmentation de 12% par rapport à la période précédente.
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Destinations à forte croissance
                        </Typography>
                        <Typography variant="body2">
                          Les destinations du Moyen-Orient et d'Europe montrent la plus forte croissance. 
                          Les réservations pour Dubaï ont augmenté de 28% ce trimestre.
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Recommandations
                        </Typography>
                        <Typography variant="body2">
                          Augmenter l'offre de forfaits premium et renforcer les partenariats avec les fournisseurs au Moyen-Orient 
                          pour capitaliser sur les tendances actuelles.
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

          {/* Rentabilité Tab */}
        <TabPanel value={activeTab} index={2}>
            <Grid container spacing={3}>
            <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Analyse de rentabilité
              </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Examen détaillé des marges, des coûts et de la rentabilité par service et par forfait
                </Typography>
                
                <Box sx={{ mb: 4, p: 2, bgcolor: 'rgba(46, 125, 50, 0.04)', borderRadius: 2, border: '1px solid rgba(46, 125, 50, 0.1)' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1, color: 'success.main' }}>
                    <TrendingUpIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Synthèse de la rentabilité
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ShowChartIcon color="success" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{(calculatedFinancialData?.kpis?.margin || 0).toFixed(1)}%</Typography>
                          <Typography variant="caption" color="text.secondary">Marge brute globale</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{calculatedFinancialData?.kpis?.marginChange || '+0.0%'}</Typography>
                          <Typography variant="caption" color="text.secondary">Évolution de la marge</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PieChartIcon color="primary" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{formatCurrency(calculatedFinancialData?.kpis?.profit?.current || 0)}</Typography>
                          <Typography variant="caption" color="text.secondary">Bénéfice net</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PercentIcon color="info" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{(calculatedFinancialData?.kpis?.roi?.current || 0).toFixed(1)}%</Typography>
                          <Typography variant="caption" color="text.secondary">ROI</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
            </Grid>
            
              {/* Costs Structure Chart - Replacing duplicate Transaction Analysis */}
              <Grid item xs={12} md={6}>
                {renderChart("Structure des Coûts par Catégorie", 
                  <ResponsiveContainer width="100%" height={350}>
                    <RechartsBarChart data={calculatedFinancialData?.costBreakdown || (financialData?.transactions ? [
                      // Convert real transaction data into cost breakdown categories
                      ...(financialData.transactions
                        .filter(t => t.type === 'Dépense')
                        .reduce((acc, t) => {
                          const category = t.categorie || 'Autres';
                          const existingIndex = acc.findIndex(item => item.name === category);
                          if (existingIndex >= 0) {
                            acc[existingIndex].value += t.montant;
                          } else {
                            acc.push({name: category, value: t.montant});
                          }
                          return acc;
                        }, [])
                        .sort((a, b) => b.value - a.value)
                        .slice(0, 5))
                    ] : [])}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(value) => `${Math.round(value/1000)}k`} />
                      <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="value" fill={colors.warning} name="Montant" />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                )}
            </Grid>
            
              {/* Margin Evolution */}
              <Grid item xs={12} md={6}>
                {renderChart("Évolution des Marges", 
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={calculatedFinancialData?.profitabilityAnalysis?.trends || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis tickFormatter={(value) => `${value}%`} domain={['auto', 'auto']} />
                      <RechartsTooltip formatter={(value) => `${value}%`} />
                      <Legend />
                      <Line type="monotone" dataKey="margin" stroke={colors.success} strokeWidth={2} name="Marge (%)" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
            </Grid>
            
              {/* Cost Structure */}
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)', height: '100%' }}>
                  <CardHeader 
                    title="Structure des coûts" 
                    sx={{ 
                      pb: 0, 
                      '& .MuiCardHeader-title': { 
                        fontSize: '1rem', 
                        fontWeight: 'bold' 
                      } 
                    }} 
                  />
                  <CardContent>
                    <TableContainer sx={{ maxHeight: 350 }}>
                      <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                            <TableCell>Catégorie</TableCell>
                            <TableCell align="right">Montant</TableCell>
                            <TableCell align="right">% du Total</TableCell>
                            <TableCell align="right">Évolution</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                          <TableRow>
                            <TableCell>Coûts fournisseurs</TableCell>
                            <TableCell align="right">{formatCurrency(2650000)}</TableCell>
                            <TableCell align="right">73.6%</TableCell>
                          <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <ArrowUpwardIcon fontSize="small" sx={{ color: 'error.main', mr: 0.5 }} />
                                <Typography variant="body2" color="error.main">4.2%</Typography>
                              </Box>
                          </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Salaires & charges</TableCell>
                            <TableCell align="right">{formatCurrency(520000)}</TableCell>
                            <TableCell align="right">14.4%</TableCell>
                          <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <ArrowUpwardIcon fontSize="small" sx={{ color: 'error.main', mr: 0.5 }} />
                                <Typography variant="body2" color="error.main">3.1%</Typography>
                              </Box>
                          </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Marketing</TableCell>
                            <TableCell align="right">{formatCurrency(210000)}</TableCell>
                            <TableCell align="right">5.8%</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <ArrowDownwardIcon fontSize="small" sx={{ color: 'success.main', mr: 0.5 }} />
                                <Typography variant="body2" color="success.main">2.3%</Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Locaux & équipement</TableCell>
                            <TableCell align="right">{formatCurrency(120000)}</TableCell>
                            <TableCell align="right">3.3%</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <ArrowUpwardIcon fontSize="small" sx={{ color: 'error.main', mr: 0.5 }} />
                                <Typography variant="body2" color="error.main">1.8%</Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Autres frais</TableCell>
                            <TableCell align="right">{formatCurrency(100000)}</TableCell>
                            <TableCell align="right">2.8%</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <ArrowDownwardIcon fontSize="small" sx={{ color: 'success.main', mr: 0.5 }} />
                                <Typography variant="body2" color="success.main">1.5%</Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Most Profitable Packages */}
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)', height: '100%' }}>
                  <CardHeader 
                    title="Forfaits les plus rentables" 
                    sx={{ 
                      pb: 0, 
                      '& .MuiCardHeader-title': { 
                        fontSize: '1rem', 
                        fontWeight: 'bold' 
                      } 
                    }} 
                  />
                  <CardContent>
                    <TableContainer sx={{ maxHeight: 350 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Forfait</TableCell>
                            <TableCell>Destination</TableCell>
                            <TableCell align="right">Prix (DZD)</TableCell>
                            <TableCell align="right">Marge (%)</TableCell>
                            <TableCell align="right">Contribution</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(calculatedFinancialData?.topPackages || []).slice(0, 5).sort((a, b) => b.marge - a.marge).map((pack) => (
                            <TableRow key={pack.nom}>
                              <TableCell>{pack.nom}</TableCell>
                              <TableCell>{pack.destination}</TableCell>
                              <TableCell align="right">{formatCurrency(pack.prix)}</TableCell>
                              <TableCell align="right">{pack.marge.toFixed(1)}%</TableCell>
                              <TableCell align="right">{formatCurrency(pack.revenus * (pack.marge / 100))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                  </CardContent>
              </Card>
            </Grid>

              {/* Profitability Analysis */}
            <Grid item xs={12}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)', p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    <AssessmentIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Analyse et recommandations de rentabilité
              </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="success.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Points forts
                        </Typography>
                        <Typography variant="body2">
                          Les services Omra et Visas présentent les meilleures marges avec respectivement 35.2% et 42.5%.
                          La tendance globale des marges est positive avec une progression de 5% sur l'année.
                        </Typography>
                      </Paper>
            </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="error.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Points d'amélioration
                  </Typography>
                        <Typography variant="body2">
                          Les marges sur les vols restent faibles (22.5%). 
                          Les coûts fournisseurs augmentent plus rapidement que les prix de vente (+4.2% vs +3.8%).
                  </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Actions recommandées
                  </Typography>
                        <Typography variant="body2">
                          Renégocier les contrats avec les fournisseurs aériens, augmenter la part des forfaits Omra et Visas dans le portefeuille,
                          et optimiser les coûts marketing en favorisant le digital.
                        </Typography>
                      </Paper>
            </Grid>
                  </Grid>
                </Card>
            </Grid>
          </Grid>
        </TabPanel>

          {/* Trésorerie Tab */}
          <TabPanel value={activeTab} index={3}>
            <Grid container spacing={3}>
            <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Analyse de trésorerie
              </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Vue détaillée des flux de trésorerie, des liquidités et des projections
                </Typography>
                
                <Box sx={{ mb: 4, p: 2, bgcolor: 'rgba(2, 136, 209, 0.04)', borderRadius: 2, border: '1px solid rgba(2, 136, 209, 0.1)' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1, color: 'info.main' }}>
                    <WalletIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Situation actuelle de trésorerie
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccountBalanceWallet color="info" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{formatCurrency(calculatedFinancialData?.kpis?.cashFlow?.current || 0)}</Typography>
                          <Typography variant="caption" color="text.secondary">Solde actuel</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CompareArrowsIcon color="success" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>+{(calculatedFinancialData?.kpis?.cashFlow?.growth || 0).toFixed(1)}%</Typography>
                          <Typography variant="caption" color="text.secondary">Variation</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccessTimeIcon color="warning" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>38 jours</Typography>
                          <Typography variant="caption" color="text.secondary">Délai de recouvrement</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccountBalanceIcon color="error" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>43 jours</Typography>
                          <Typography variant="caption" color="text.secondary">Délai de règlement</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
            </Grid>
            
              {/* Cash Flow Timeline */}
              <Grid item xs={12}>
                {renderChart("Évolution de la Trésorerie", 
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart data={[
                      { month: 'Jan', entrees: 350000, sorties: 320000, solde: 30000, cumulatif: 1500000 },
                      { month: 'Fév', entrees: 420000, sorties: 350000, solde: 70000, cumulatif: 1570000 },
                      { month: 'Mar', entrees: 380000, sorties: 360000, solde: 20000, cumulatif: 1590000 },
                      { month: 'Avr', entrees: 420000, sorties: 390000, solde: 30000, cumulatif: 1620000 },
                      { month: 'Mai', entrees: 480000, sorties: 410000, solde: 70000, cumulatif: 1690000 },
                      { month: 'Jun', entrees: 520000, sorties: 440000, solde: 80000, cumulatif: 1770000 },
                      { month: 'Jul', entrees: 550000, sorties: 470000, solde: 80000, cumulatif: 1850000 },
                      { month: 'Aoû', entrees: 500000, sorties: 480000, solde: 20000, cumulatif: 1870000 },
                      { month: 'Sep', entrees: 450000, sorties: 420000, solde: 30000, cumulatif: 1900000 },
                      { month: 'Oct', entrees: 400000, sorties: 380000, solde: 20000, cumulatif: 1920000 },
                      { month: 'Nov', entrees: 380000, sorties: 350000, solde: 30000, cumulatif: 1950000 },
                      { month: 'Déc', entrees: 420000, sorties: 370000, solde: 50000, cumulatif: 2000000 }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" tickFormatter={(value) => formatCurrency(value, true)} />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => formatCurrency(value, true)} />
                      <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                      <Bar yAxisId="left" dataKey="entrees" fill={colors.success} name="Entrées" />
                      <Bar yAxisId="left" dataKey="sorties" fill={colors.error} name="Sorties" />
                      <Line yAxisId="left" type="monotone" dataKey="solde" stroke={colors.warning} strokeWidth={2} name="Solde mensuel" />
                      <Line yAxisId="right" type="monotone" dataKey="cumulatif" stroke={colors.info} strokeWidth={3} name="Trésorerie cumulative" />
                  </ComposedChart>
                </ResponsiveContainer>
                , true)}
            </Grid>
            
              {/* Cash Flow Indicators */}
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)', height: '100%' }}>
                  <CardHeader 
                    title="Indicateurs de santé financière" 
                    sx={{ 
                      pb: 0, 
                      '& .MuiCardHeader-title': { 
                        fontSize: '1rem', 
                        fontWeight: 'bold' 
                      } 
                    }} 
                  />
                  <CardContent>
                    <TableContainer sx={{ maxHeight: 350 }}>
                      <Table size="small">
                    <TableHead>
                      <TableRow>
                            <TableCell>Indicateur</TableCell>
                            <TableCell align="right">Valeur actuelle</TableCell>
                            <TableCell align="right">Objectif</TableCell>
                            <TableCell align="right">Statut</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                          <TableRow>
                            <TableCell>Ratio de liquidité immédiate</TableCell>
                            <TableCell align="right">1.8</TableCell>
                            <TableCell align="right">{'>'} 1.5</TableCell>
                          <TableCell align="right">
                              <Chip size="small" label="Bon" color="success" />
                          </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Ratio de couverture des charges</TableCell>
                            <TableCell align="right">4.2</TableCell>
                            <TableCell align="right">{'>'} 3</TableCell>
                          <TableCell align="right">
                              <Chip size="small" label="Bon" color="success" />
                          </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Délai de recouvrement</TableCell>
                            <TableCell align="right">38 jours</TableCell>
                            <TableCell align="right">{'<'} 30 jours</TableCell>
                          <TableCell align="right">
                              <Chip size="small" label="À améliorer" color="warning" />
                          </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Délai de règlement fournisseurs</TableCell>
                            <TableCell align="right">43 jours</TableCell>
                            <TableCell align="right">{'<'} 45 jours</TableCell>
                          <TableCell align="right">
                              <Chip size="small" label="Bon" color="success" />
                          </TableCell>
                        </TableRow>
                          <TableRow>
                            <TableCell>Taux de trésorerie disponible</TableCell>
                            <TableCell align="right">22.5%</TableCell>
                            <TableCell align="right">{'>'} 20%</TableCell>
                            <TableCell align="right">
                              <Chip size="small" label="Bon" color="success" />
                            </TableCell>
                          </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                  </CardContent>
              </Card>
            </Grid>

              {/* Cash Flow Projection */}
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)', height: '100%' }}>
                  <CardHeader 
                    title="Projection de trésorerie (90 jours)" 
                    sx={{ 
                      pb: 0, 
                      '& .MuiCardHeader-title': { 
                        fontSize: '1rem', 
                        fontWeight: 'bold' 
                      } 
                    }} 
                  />
                  <CardContent>
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Solde projeté à 90 jours
              </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                          {formatCurrency(2180000)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Variation
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ArrowUpwardIcon fontSize="small" color="success" />
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            +9.0%
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Entrées et sorties prévues
                    </Typography>
                    <ResponsiveContainer width="100%" height={240}>
                      <AreaChart
                        data={[
                          { mois: 'Ce mois', entrees: 450000, sorties: 380000 },
                          { mois: 'Mois +1', entrees: 520000, sorties: 430000 },
                          { mois: 'Mois +2', entrees: 480000, sorties: 410000 },
                          { mois: 'Mois +3', entrees: 530000, sorties: 440000 }
                        ]}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="mois" />
                        <YAxis tickFormatter={(value) => formatCurrency(value, true)} />
                        <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                        <Area type="monotone" dataKey="entrees" stackId="1" stroke={colors.success} fill={`${colors.success}22`} name="Entrées" />
                        <Area type="monotone" dataKey="sorties" stackId="2" stroke={colors.error} fill={`${colors.error}22`} name="Sorties" />
                  </AreaChart>
                </ResponsiveContainer>
                  </CardContent>
                </Card>
            </Grid>
            
              {/* Recommendations */}
            <Grid item xs={12}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)', p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    <TipsAndUpdatesIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Recommandations pour optimiser la trésorerie
                  </Typography>
              <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="info.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Réduction du délai de recouvrement
                      </Typography>
                        <Typography variant="body2">
                          Mettre en place un système de paiement anticipé avec remises pour les clients réguliers.
                          Objectif: réduire de 38 à 30 jours le délai moyen.
                      </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="info.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Optimisation des flux fournisseurs
                      </Typography>
                        <Typography variant="body2">
                          Renégocier les contrats avec les principaux fournisseurs pour aligner les échéances de paiement
                          sur les encaissements clients.
                      </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="info.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Réserve de sécurité
                      </Typography>
                        <Typography variant="body2">
                          Maintenir une réserve de trésorerie équivalente à 2 mois de charges fixes
                          pour faire face aux imprévus et saisir les opportunités d'investissement.
                        </Typography>
                      </Paper>
                  </Grid>
              </Grid>
                </Card>
            </Grid>
          </Grid>
        </TabPanel>

          {/* Charges Tab */}
          <TabPanel value={activeTab} index={4}>
            <Grid container spacing={3}>
            <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Analyse des charges
              </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Vue détaillée des dépenses, évolution et comparaison avec les budgets
                </Typography>
                
                <Box sx={{ mb: 4, p: 2, bgcolor: 'rgba(211, 47, 47, 0.04)', borderRadius: 2, border: '1px solid rgba(211, 47, 47, 0.1)' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1, color: 'error.main' }}>
                    <PaymentIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    État des charges actuelles
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AttachMoney color="error" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{formatCurrency(calculatedFinancialData?.kpis?.expenses?.current || 0)}</Typography>
                          <Typography variant="caption" color="text.secondary">Total des charges</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ArrowUpwardIcon color="error" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>+{(calculatedFinancialData?.kpis?.expenses?.growth || 0).toFixed(1)}%</Typography>
                          <Typography variant="caption" color="text.secondary">Évolution annuelle</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CompareArrowsIcon color={calculatedFinancialData?.kpis?.expenses?.current > calculatedFinancialData?.kpis?.expenses?.target ? "error" : "success"} sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{formatCurrency(calculatedFinancialData?.kpis?.expenses?.target || 0)}</Typography>
                          <Typography variant="caption" color="text.secondary">Budget prévu</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PercentIcon color="primary" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{((calculatedFinancialData?.kpis?.expenses?.current / calculatedFinancialData?.kpis?.revenue?.current) * 100 || 0).toFixed(1)}%</Typography>
                          <Typography variant="caption" color="text.secondary">Ratio Charges/Revenus</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
            </Grid>
            
              {/* Expenses by Category */}
              <Grid item xs={12} md={6}>
                {renderChart("Répartition des charges par catégorie", 
                  <ResponsiveContainer width="100%" height={350}>
                    <RechartsPieChart>
                      <Pie
                        data={[
                          { name: 'Coûts fournisseurs', value: 2650000 },
                          { name: 'Salaires & charges', value: 520000 },
                          { name: 'Marketing', value: 210000 },
                          { name: 'Locaux', value: 120000 },
                          { name: 'Autres frais', value: 100000 }
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {[0, 1, 2, 3, 4].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={colors.chart[index % colors.chart.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                )}
              </Grid>

              {/* Expenses Evolution */}
              <Grid item xs={12} md={6}>
                {renderChart("Évolution des charges", 
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={[
                      { month: 'Jan', charges: 290000, budget: 280000 },
                      { month: 'Fév', charges: 300000, budget: 285000 },
                      { month: 'Mar', charges: 285000, budget: 290000 },
                      { month: 'Avr', charges: 305000, budget: 295000 },
                      { month: 'Mai', charges: 310000, budget: 300000 },
                      { month: 'Jun', charges: 330000, budget: 305000 },
                      { month: 'Jul', charges: 340000, budget: 310000 },
                      { month: 'Aoû', charges: 345000, budget: 315000 },
                      { month: 'Sep', charges: 330000, budget: 320000 },
                      { month: 'Oct', charges: 315000, budget: 325000 },
                      { month: 'Nov', charges: 300000, budget: 330000 },
                      { month: 'Déc', charges: 290000, budget: 335000 }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(value) => formatCurrency(value, true)} />
                      <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Line type="monotone" dataKey="charges" stroke={colors.error} strokeWidth={2} name="Charges réelles" />
                      <Line type="monotone" dataKey="budget" stroke={colors.info} strokeWidth={2} strokeDasharray="5 5" name="Budget" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Grid>

              {/* Expense Details Table */}
            <Grid item xs={12}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)', height: '100%' }}>
                  <CardHeader 
                    title="Détail des postes de dépenses" 
                    sx={{ 
                      pb: 0, 
                      '& .MuiCardHeader-title': { 
                        fontSize: '1rem', 
                        fontWeight: 'bold' 
                      } 
                    }} 
                  />
                  <CardContent>
                    <TableContainer sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Catégorie</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Montant</TableCell>
                            <TableCell align="right">Budget</TableCell>
                            <TableCell align="right">Écart</TableCell>
                            <TableCell align="right">Statut</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell rowSpan={3}>Coûts fournisseurs</TableCell>
                            <TableCell>Transport aérien</TableCell>
                            <TableCell align="right">{formatCurrency(1450000)}</TableCell>
                            <TableCell align="right">{formatCurrency(1400000)}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <ArrowUpwardIcon fontSize="small" sx={{ color: 'error.main', mr: 0.5 }} />
                                <Typography variant="body2" color="error.main">+3.6%</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Chip size="small" label="Dépassement" color="error" />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Hébergement</TableCell>
                            <TableCell align="right">{formatCurrency(850000)}</TableCell>
                            <TableCell align="right">{formatCurrency(820000)}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <ArrowUpwardIcon fontSize="small" sx={{ color: 'error.main', mr: 0.5 }} />
                                <Typography variant="body2" color="error.main">+3.7%</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Chip size="small" label="Dépassement" color="error" />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Services locaux</TableCell>
                            <TableCell align="right">{formatCurrency(350000)}</TableCell>
                            <TableCell align="right">{formatCurrency(380000)}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <ArrowDownwardIcon fontSize="small" sx={{ color: 'success.main', mr: 0.5 }} />
                                <Typography variant="body2" color="success.main">-7.9%</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Chip size="small" label="Économie" color="success" />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell rowSpan={2}>Salaires & charges</TableCell>
                            <TableCell>Personnel permanent</TableCell>
                            <TableCell align="right">{formatCurrency(420000)}</TableCell>
                            <TableCell align="right">{formatCurrency(420000)}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <Typography variant="body2">0.0%</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Chip size="small" label="Conforme" color="primary" />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Personnel temporaire</TableCell>
                            <TableCell align="right">{formatCurrency(100000)}</TableCell>
                            <TableCell align="right">{formatCurrency(90000)}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <ArrowUpwardIcon fontSize="small" sx={{ color: 'error.main', mr: 0.5 }} />
                                <Typography variant="body2" color="error.main">+11.1%</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Chip size="small" label="Dépassement" color="error" />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell rowSpan={2}>Marketing</TableCell>
                            <TableCell>Digital</TableCell>
                            <TableCell align="right">{formatCurrency(130000)}</TableCell>
                            <TableCell align="right">{formatCurrency(150000)}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <ArrowDownwardIcon fontSize="small" sx={{ color: 'success.main', mr: 0.5 }} />
                                <Typography variant="body2" color="success.main">-13.3%</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Chip size="small" label="Économie" color="success" />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Traditionnel</TableCell>
                            <TableCell align="right">{formatCurrency(80000)}</TableCell>
                            <TableCell align="right">{formatCurrency(70000)}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <ArrowUpwardIcon fontSize="small" sx={{ color: 'error.main', mr: 0.5 }} />
                                <Typography variant="body2" color="error.main">+14.3%</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Chip size="small" label="Dépassement" color="error" />
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                    </Card>
            </Grid>
            
              {/* Expense Optimization */}
            <Grid item xs={12}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)', p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    <TipsAndUpdatesIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Optimisations recommandées
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="error.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Transport aérien
                      </Typography>
                      <Typography variant="body2">
                          Renforcer les partenariats avec des compagnies aériennes clés pour obtenir de meilleures conditions
                          tarifaires et des remises sur volume. Économie potentielle: 8-12%.
                      </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="error.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Personnel temporaire
                      </Typography>
                      <Typography variant="body2">
                          Mettre en place une planification des ressources plus efficace avec une anticipation
                          des besoins saisonniers. Économie potentielle: 15%.
                      </Typography>
                      </Paper>
                  </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="error.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Marketing traditionnel
                      </Typography>
                      <Typography variant="body2">
                          Réduire progressivement les budgets marketing traditionnels au profit des canaux
                          digitaux plus rentables. Économie potentielle: 20%.
                      </Typography>
                      </Paper>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

          {/* Add notification snackbar */}
          <Snackbar
            open={notification.open}
            autoHideDuration={6000}
            onClose={handleCloseNotification}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert 
              onClose={handleCloseNotification} 
              severity={notification.severity}
              variant="filled" 
              sx={{ width: '100%' }}
            >
              {notification.message}
                        </Alert>
          </Snackbar>
        </>
        </div>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Typography variant="h5" color="text.secondary">Aucune donnée financière disponible.</Typography>
        </Box>
      )}
    </Box>
  );
};

export default FinancesReports;